/**
 * Per-artist merch deals — the bottom half of the Inventory Configuration tab.
 *
 * The event-level merch fields above this (payout source, tax) are part of the
 * event form and are saved by eventEditAction. THIS SECTION IS NOT. Everything
 * here is per (event, vendor) and writes straight to Merch Manager's tables with
 * its own Apollo mutations + toasts, the same way OrderAheadOverview's
 * event-config cards do. That also means:
 *
 *   - No input in here may carry a `name` attribute (or it gets swept into the
 *     event form's FormData and spread into the events _set mutation as a
 *     column that doesn't exist). Components that require a name are passed
 *     name="" — the SubEventManager convention.
 *   - Every button is type="button" so it can't submit the surrounding form.
 *
 * Three tables, one rule each that GraphQL won't enforce for you:
 *
 *   location_menu           the (SINGULAR) join between locations and menus, and
 *                           many-to-many BOTH ways — one menu can serve several
 *                           locations, one location can carry several menus. Attach and
 *                           detach individual join rows by id; never rewrite the
 *                           set for a location, or you drop another module's
 *                           menu off a shared location.
 *   merch_vendor_deal       flat splits have a DB CHECK totalling exactly 100,
 *                           on numeric(5,2) columns — round each share to 2dp
 *                           BEFORE adding, or you green-light a deal Postgres
 *                           rejects with a raw constraint name.
 *   vendor_settlement(_line) once locked_at is set the settlement is signed:
 *                           split and fees are frozen. A delete against a
 *                           locked settlement comes back affected_rows: 0
 *                           rather than an error, so 0 is a failure.
 *
 * LAYOUT RULES — every block in this file follows the same three, so a button
 * is always where the last one was:
 *
 *   1. A block's primary action sits TOP-RIGHT of that block's header row,
 *      on the same line as the block title (Add artist, Add location, Save
 *      split, Add fee, Create menu on a location card).
 *   2. Row-level actions are RIGHT-ALIGNED on their own row (Detach a menu,
 *      Attach a menu, remove a fee line).
 *   3. Modal footers put the primary on the right and Cancel to its left —
 *      MerchModal's flex-row-reverse does that from source order
 *      [primary, cancel].
 *
 * COLOUR — PrimaryButton (bg-primary-600, Ronin red) is for the affirmative
 * action that writes something. TertiaryButton (grey) is Cancel/dismiss ONLY.
 * SecondaryButton (white/outline) is for recovery actions like Retry.
 * Destructive actions (detach, remove a fee) are deliberately NOT red-filled —
 * a filled red button reads as "the thing to press". They are red *text* /
 * icons instead, so they read as destructive rather than primary.
 */
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  BuildingStorefrontIcon,
  ListBulletIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import CustomSelect from "../../components/customSelect";
import { Input } from "../../components/inputs/Input";
import PriceInput from "../../components/inputs/PriceInput";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "../../components/Button";
import { userStore } from "../../store/user";
import { VendorType } from "../../types/vendor";
import { GET_LOCATION_TYPE } from "../locations/queries";
import {
  GET_EVENT_LOCATION_POOL,
  GET_EVENT_MERCH_ARTISTS,
  GET_EVENT_MERCH_DEALS,
  GET_EVENT_MERCH_CONFIG,
  GET_EVENT_MERCH_MANAGERS,
  GET_MERCH_DEAL_MANAGER_CANDIDATES,
  GET_ORG_ITEM_CATEGORIES,
  GET_MERCH_ARTIST_USAGE,
  GET_MERCH_LOCATION_USAGE,
  GET_MERCH_VENDOR_SETUP,
} from "./queries";
import {
  CREATE_MERCH_LOCATION,
  CREATE_MERCH_LOCATION_MENU,
  CREATE_MERCH_MENU,
  CREATE_MERCH_VENDOR,
  DEACTIVATE_MERCH_LOCATION,
  DEACTIVATE_MERCH_VENDOR_DEAL,
  DEACTIVATE_MERCH_VENDOR_LOCATIONS,
  DELETE_MERCH_LOCATION_MENU,
  DELETE_VENDOR_SETTLEMENT_LINE,
  ENSURE_VENDOR_SETTLEMENT,
  INSERT_VENDOR_SETTLEMENT_LINE,
  REASSIGN_MERCH_LOCATION,
  UPDATE_MERCH_LOCATION,
  UPDATE_EVENT_MERCH_SETTINGS,
  UPDATE_MERCH_MENU,
  UPSERT_MERCH_EVENT_MANAGER,
  UPSERT_MERCH_VENDOR_DEAL,
} from "./mutation";

const client = createApolloClient();

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

export type SplitMode = "flat" | "nested";

/** Hasura hands numeric/bigint back as a number or a string depending on the
 *  column and the driver's precision handling — never trust it, always coerce. */
type Numeric = number | string | null | undefined;

interface MerchArtist {
  id: number;
  name: string;
  type: string | null;
}

interface MerchVendorDeal {
  id: number;
  vendor_id: number;
  split_mode: string;
  artist_pct: Numeric;
  organizer_pct: Numeric;
  manager_pct: Numeric;
  manager_vendor_id: number | null;
  manager_paid_in_app: boolean;
  is_active: boolean;
}

interface SettlementLine {
  id: number;
  type: string;
  label: string | null;
  flat_or_pct: string;
  rate: Numeric;
  amount_cents: Numeric;
  off_top: boolean;
  kept_by: string | null;
}

interface VendorSettlement {
  id: number;
  vendor_id: number;
  status: string | null;
  locked_at: string | null;
  lines: SettlementLine[];
}

interface ManagerCandidate {
  id: number;
  name: string;
}

interface ArtistsQueryResult {
  locations: {
    vendor_id: number | null;
    vendor: { id: number; name: string | null; type: string | null } | null;
  }[];
}

interface DealsQueryResult {
  merch_vendor_deal: MerchVendorDeal[];
  vendor_settlement: VendorSettlement[];
}

interface ManagersQueryResult {
  vendors: { id: number; name: string | null }[];
}

interface EventManagersQueryResult {
  merch_event_manager: {
    vendor_id: number;
    vendor: {
      id: number;
      name: string | null;
      is_merch_vendor_manager: boolean | null;
    } | null;
  }[];
}

interface LocationTypesQueryResult {
  dashboard_reference: {
    reference_value: string;
    reference_label: string;
  }[];
}

/** A menu owned by (event, vendor). */
interface SetupMenu {
  id: number;
  name: string | null;
}

/** One row of the location_menu JOIN, with the menu it points at. The row's own
 *  `id` is what attach/detach operate on — see DELETE_MERCH_LOCATION_MENU. */
interface SetupLocationMenu {
  id: number;
  menu_id: number;
  menu: SetupMenu | null;
}

interface SetupLocation {
  id: number;
  name: string | null;
  location_type: string | null;
  location_menus: SetupLocationMenu[];
}

interface VendorSetupQueryResult {
  locations: SetupLocation[];
  menus: SetupMenu[];
}

/** A location that already exists at this event, and the vendor that OWNS it
 *  right now — see GET_EVENT_LOCATION_POOL. */
interface PoolLocation {
  id: number;
  name: string | null;
  vendor_id: number | null;
  vendor: { id: number; name: string | null } | null;
}

interface LocationPoolQueryResult {
  locations: PoolLocation[];
}

/** How much Merch Manager has recorded against one location — see
 *  GET_MERCH_LOCATION_USAGE. The whole shape is optional because the query can
 *  fail in an environment where the table isn't tracked. */
interface LocationUsageQueryResult {
  inventory_count_line_aggregate: {
    aggregate: { count: number | null } | null;
  } | null;
  /** Sales rung at this location. Deactivating one that has taken money is not
   *  a DATA problem — orders keep their location_id and every report still
   *  resolves it — but it IS an operational one: the location drops off the POS.
   *  That deserves saying out loud before somebody does it mid-event. */
  orders_aggregate: {
    aggregate: { count: number | null } | null;
  } | null;
}

const FEE_TYPES: { value: string; label: string }[] = [
  { value: "credit_card_fee", label: "Credit card fee" },
  { value: "concessionaire", label: "Concessionaire" },
  { value: "flat_fee", label: "Flat fee" },
  { value: "custom", label: "Custom" },
];

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const num = (value: Numeric): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** numeric(5,2) — the precision Postgres will actually store. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

const fmtPct = (n: number): string => {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const currentUserId = (): number | null => {
  // OrganizationUser.id is typed string but last_updated_by is an integer
  // column; coerce rather than trusting either.
  const parsed = Number(userStore.getState().user?.id);
  return Number.isFinite(parsed) ? parsed : null;
};

const errorMessage = (error: unknown): string => {
  const raw = error instanceof Error ? error.message : String(error);
  if (raw.includes("merch_vendor_deal_flat_totals_100")) {
    return "A flat split has to total exactly 100%.";
  }
  if (raw.includes("merch_vendor_deal_distinct_parties")) {
    return "The artist can't also be the vendor manager.";
  }
  if (raw.includes("merch_vendor_deal_manager_identified")) {
    return "A manager cut needs a vendor manager selected.";
  }
  if (raw.includes("Uniqueness violation")) {
    return "That fee already exists for this artist.";
  }
  return raw;
};

/**
 * In nested mode the manager's cut is a share OF WHAT'S LEFT after the
 * organizer, so `organizer 60 + manager 60` is a legitimate deal — never sum
 * them. This turns the stored percentages into what each party actually keeps.
 */
const nestedShares = (organizerPct: number, managerPct: number) => {
  const remaining = 100 - organizerPct;
  const afterOrganizer = 1 - organizerPct / 100;
  const managerEffective = afterOrganizer * managerPct;
  return {
    remaining,
    managerEffective,
    artistEffective: remaining - managerEffective,
  };
};

/** What each party keeps, so flat and nested deals read the same way. */
const effectiveShares = (deal: MerchVendorDeal) => {
  const organizer = num(deal.organizer_pct);
  const manager = num(deal.manager_pct);
  if (deal.split_mode === "nested") {
    const shares = nestedShares(organizer, manager);
    return {
      artist: shares.artistEffective,
      organizer,
      manager: shares.managerEffective,
    };
  }
  return { artist: num(deal.artist_pct), organizer, manager };
};

const splitSummary = (deal: MerchVendorDeal | undefined): string => {
  if (!deal) return "Not set";
  const shares = effectiveShares(deal);
  return `${fmtPct(shares.artist)} / ${fmtPct(shares.organizer)} / ${fmtPct(
    shares.manager
  )}`;
};

const lineValue = (line: SettlementLine): string =>
  line.flat_or_pct === "pct"
    ? `${fmtPct(num(line.rate))}%`
    : money(num(line.amount_cents));

/**
 * The menu object Merch Manager's EnsureMenuForLocationAsync would create.
 * Every path in this file that mints a merch menu goes through here so the two
 * codebases can't drift into subtly different menus (cash + credit on, active,
 * named "<vendor> — Merch").
 */
const merchMenuInput = (eventId: number, vendorId: number, name: string) => ({
  name,
  event_id: eventId,
  vendor_id: vendorId,
  is_active: true,
  is_cash: true,
  is_credit: true,
  last_updated_by: currentUserId(),
});

/** "<vendor> — Merch", suffixed if the vendor already has a menu by that name,
 *  so a second location doesn't end up with two identically-named menus that ops
 *  then can't tell apart in the attach picker. */
const nextMerchMenuName = (vendorName: string, existing: SetupMenu[]): string => {
  const base = `${vendorName.trim() || "Merch"} — Merch`;
  const taken = new Set(
    existing.map((menu) => (menu.name ?? "").trim().toLowerCase())
  );
  if (!taken.has(base.toLowerCase())) return base;
  let n = 2;
  while (taken.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
};

// ---------------------------------------------------------------------------
// ronin-api — Merch Manager invitations
//
// POST {RONIN_API}/api/merchInvite/send. This mirrors modules/stripe_connect/
// api.ts exactly: the same VITE_RONIN_API_BASE (with the same prod fallback),
// the same dashboard JWT read from localStorage under "ron-auth", the same JSON
// POST, and the same {error}/{message} unwrapping. That module's postJson is
// not exported, so it is mirrored here rather than imported.
//
// Deliberately fire-and-forget: merch_invite may not be tracked in Hasura yet,
// so nothing here reads invite state back out of the database. What has been
// sent is remembered in component state for this page visit only.
// ---------------------------------------------------------------------------

// Fall back to https://api.roninpos.app so prod still works even if the
// env var isn't set in a given build — same as stripe_connect/api.ts.
const RONIN_API_BASE =
  (import.meta.env.VITE_RONIN_API_BASE as string | undefined) ||
  "https://api.roninpos.app";

/**
 * Where the invite email points: MERCH MANAGER's origin, not the dashboard's.
 *
 * VITE_MERCH_MANAGER_URL MUST BE SET PER ENVIRONMENT (a staging build has to
 * carry the staging Merch Manager URL, a prod build the prod one). The
 * window.location.origin fallback only keeps the button usable in local dev
 * where the two share a host — anywhere they don't, it would mail people a
 * link into the admin app instead.
 */
const merchManagerAppUrl = (): string =>
  (import.meta.env.VITE_MERCH_MANAGER_URL as string | undefined) ||
  window.location.origin;

export type MerchInviteRole = "artist" | "vendor_manager" | "organizer";

interface MerchInviteResponse {
  sent: boolean;
  email: string;
  /** True when the address already has a Ronin login. There is no invite for
   *  them to accept — they simply gained access — and the toast has to say so,
   *  or they sit waiting for an email that never arrives. */
  existingUser: boolean;
  expiresAt: string | null;
}

const sendMerchInvite = async (body: {
  eventId: number;
  email: string;
  role: MerchInviteRole;
  vendorId: number;
}): Promise<MerchInviteResponse> => {
  const path = "/api/merchInvite/send";
  const token = localStorage.getItem("ron-auth");
  let res: Response;
  try {
    res = await fetch(`${RONIN_API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ...body, appUrl: merchManagerAppUrl() }),
    });
  } catch (error: any) {
    // Network-level failure (no response) — usually CORS, DNS, or backend down.
    throw new Error(
      `Network error calling ${RONIN_API_BASE}${path}: ${
        error?.message || error
      }. Check that ronin-api is running and CORS allows ${
        window.location.origin
      }.`
    );
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText} from ${path}`;
    try {
      const data = await res.json();
      if (data?.error) message = `${res.status}: ${data.error}`;
      else if (data?.message) message = `${res.status}: ${data.message}`;
    } catch {
      if (res.status === 404) {
        message += " — endpoint not found. Is the ronin-api merchInvite branch deployed?";
      }
    }
    throw new Error(message);
  }
  const data = (await res.json()) as MerchInviteResponse & { error?: string };
  // The endpoint reports refusals in the body as well as by status.
  if (data?.error) throw new Error(data.error);
  return data;
};

/** Who an invite is being sent to. Held by MerchVendorDeals rather than by the
 *  button, so the "already sent" memory survives collapsing an artist panel. */
interface InviteTarget {
  vendorId: number;
  role: MerchInviteRole;
  /** Named in the modal so nobody invites the wrong vendor. */
  vendorName: string;
  /** Prefill, when we happen to know it. The setup query doesn't return
   *  poc_email, so in practice this is blank and gets typed in. */
  email?: string;
}

const inviteKey = (role: MerchInviteRole, vendorId: number): string =>
  `${role}:${vendorId}`;

const INVITE_ROLE_LABEL: Record<MerchInviteRole, string> = {
  artist: "artist",
  vendor_manager: "vendor manager",
  organizer: "organizer",
};

/**
 * locations.location_type is a dashboard_reference value, not a free string —
 * LocationCreate makes it a required select, so both places in this file that
 * create a location mirror it rather than hardcoding a value that may not exist
 * in this org's reference table.
 *
 * `active` gates the fetch so a closed modal / collapsed panel doesn't query.
 * Failure is non-fatal by design: no options means the caller omits the column
 * and lets the DB default (or its error) decide instead of us guessing.
 */
const useLocationTypes = (
  active: boolean,
  organizationId: number | null | undefined
) => {
  const [locationTypes, setLocationTypes] = useState<
    { value: string; label: string }[]
  >([]);
  const [locationType, setLocationType] = useState<string>("");

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await client.query<LocationTypesQueryResult>({
          query: GET_LOCATION_TYPE,
          variables: {
            where: {
              organization_id: { _in: [0, organizationId ?? 0] },
              is_active: { _eq: true },
              reference_type: { _eq: "locations_location_type" },
            },
          },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const options = (data?.dashboard_reference ?? []).map((row) => ({
          value: row.reference_value,
          label: row.reference_label,
        }));
        setLocationTypes(options);
        const preferred =
          options.find(
            (option) =>
              /merch/i.test(option.label) || /merch/i.test(option.value)
          ) ?? options[0];
        setLocationType((current) => current || preferred?.value || "");
      } catch {
        if (!cancelled) setLocationTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, organizationId]);

  return { locationTypes, locationType, setLocationType };
};

// ---------------------------------------------------------------------------
// small shared inputs (deliberately unnamed — see the file header)
// ---------------------------------------------------------------------------

const PercentField = ({
  label,
  value,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: string;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="relative mt-1 rounded-md shadow-sm">
      <input
        type="number"
        step={0.01}
        min={0}
        max={100}
        inputMode="decimal"
        placeholder="0"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onWheel={(e) => e.currentTarget.blur()}
        className="block w-full rounded-md border-gray-300 pr-8 focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
      />
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <span className="text-gray-500 sm:text-sm">%</span>
      </div>
    </div>
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const Segmented = ({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => (
  <span className="inline-flex rounded-md shadow-sm">
    {options.map((option, index) => (
      <button
        key={option.value}
        type="button"
        disabled={disabled}
        onClick={() => onChange(option.value)}
        className={`relative -ml-px inline-flex items-center border border-gray-300 px-3 py-1.5 text-sm font-medium first:ml-0 first:rounded-l-md last:rounded-r-md focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 ${
          value === option.value
            ? "bg-primary-600 text-white border-primary-600"
            : "bg-white text-gray-700 hover:bg-gray-50"
        } ${index === 0 ? "rounded-l-md" : ""}`}
      >
        {option.label}
      </button>
    ))}
  </span>
);

/**
 * The "which of these two things am I looking at" marker.
 *
 * A location and the menu hanging off it are both just a name on a line, and ops
 * could not tell them apart. Solid dark chip = the physical LOCATION, outlined
 * light chip = a MENU on it — different fill, not just different words, so it
 * reads at a glance without stopping to compare indentation.
 */
const TypeTag = ({ kind }: { kind: "location" | "menu" }) =>
  kind === "location" ? (
    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
      <BuildingStorefrontIcon className="h-3 w-3" aria-hidden="true" />
      Location
    </span>
  ) : (
    <span className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
      <ListBulletIcon className="h-3 w-3" aria-hidden="true" />
      Menu
    </span>
  );

// ---------------------------------------------------------------------------
// modals
//
// These are headlessui Dialogs, so they render through a portal at the end of
// <body> — physically OUTSIDE the surrounding react-router <Form>. Every field
// in them still gets name="" anyway: the portal is an implementation detail of
// the dialog library, not a guarantee, and one `name` sneaking into the event
// FormData becomes a nonexistent column in the events _set mutation.
// ---------------------------------------------------------------------------

const MerchModal = ({
  open,
  title,
  description,
  busy,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  description?: string;
  busy?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) => (
  <Transition.Root show={open} as={Fragment}>
    <Dialog
      as="div"
      className="relative z-20"
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </Transition.Child>

      <div className="fixed inset-0 z-20 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="relative w-full transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
              <Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                {title}
              </Dialog.Title>
              {description && (
                <p className="mt-1 text-xs text-gray-500">{description}</p>
              )}
              <div className="mt-4">{children}</div>
              <div className="mt-5 flex flex-row-reverse items-center sm:mt-6">
                {footer}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition.Root>
);

/**
 * Add an artist to this event — either a brand new vendor row, or an existing
 * org vendor that just hasn't been placed at this event yet.
 *
 * Both paths end in the SAME write: a location. The artist list is derived from
 * `locations`, so the location is the thing that puts a vendor on the event;
 * creating the vendor alone accomplishes nothing visible here.
 */
const AddArtistModal = ({
  open,
  eventId,
  organizationId,
  availableVendors,
  onClose,
  onDone,
}: {
  open: boolean;
  eventId: number;
  organizationId: number | null | undefined;
  /** Active org vendors that are NOT already at this event. */
  availableVendors: ManagerCandidate[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) => {
  const [tab, setTab] = useState<"new" | "existing">("new");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationNameTouched, setLocationNameTouched] = useState(false);
  const [existingVendorId, setExistingVendorId] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const { locationTypes, locationType, setLocationType } = useLocationTypes(
    open,
    organizationId
  );

  const selectedVendor = availableVendors.find(
    (vendor) => vendor.id === existingVendorId
  );
  const vendorLabel = tab === "new" ? name.trim() : selectedVendor?.name ?? "";
  const derivedLocationName = vendorLabel ? `${vendorLabel} Merch` : "";
  const locationValue = locationNameTouched
    ? locationName
    : derivedLocationName;

  const hasVendor = tab === "new" ? name.trim() !== "" : existingVendorId !== 0;
  const needsLocationType = locationTypes.length > 0 && locationType === "";
  const canCreateVendor = Boolean(organizationId) || tab === "existing";
  const valid =
    hasVendor &&
    locationValue.trim() !== "" &&
    !needsLocationType &&
    canCreateVendor;

  const reset = () => {
    setTab("new");
    setName("");
    setEmail("");
    setLocationName("");
    setLocationNameTouched(false);
    setExistingVendorId(0);
    setFormKey((key) => key + 1);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    let vendorId = existingVendorId;
    let createdVendorName = "";
    try {
      if (tab === "new") {
        const created = await client.mutate<{
          insert_vendors_one: { id: number } | null;
        }>({
          mutation: CREATE_MERCH_VENDOR,
          variables: {
            object: {
              name: name.trim(),
              type: VendorType.ARTIST_MERCH,
              organization_id: organizationId,
              poc_email: email.trim() === "" ? null : email.trim(),
              is_active: true,
              // Deliberately NOT setting revenue_share or flat_fee: a null
              // revenue_share is what makes an artist with no configured deal
              // refuse to settle instead of silently settling at zero.
              last_updated_by: currentUserId(),
            },
          },
        });
        const newId = created.data?.insert_vendors_one?.id;
        if (!newId) throw new Error("The artist record wasn't created.");
        vendorId = newId;
        createdVendorName = name.trim();
      }

      let newLocationId: number | null = null;
      try {
        const loc = await client.mutate<{
          insert_locations_one: { id: number } | null;
        }>({
          mutation: CREATE_MERCH_LOCATION,
          variables: {
            object: {
              name: locationValue.trim(),
              event_id: eventId,
              vendor_id: vendorId,
              ...(locationType === "" ? {} : { location_type: locationType }),
              is_active: true,
              last_updated_by: currentUserId(),
            },
          },
        });
        newLocationId = loc.data?.insert_locations_one?.id ?? null;
      } catch (error) {
        // The vendor now exists but nothing ties it to this event, so it will
        // NOT show up in the list below. Half-done is worse than not done —
        // say exactly what happened instead of a generic failure.
        if (createdVendorName !== "") {
          toast.error(
            `Created the artist "${createdVendorName}", but couldn't add them to this event: ${errorMessage(
              error
            )} They won't appear below until a location is added for them.`
          );
          // Close: the vendor row is already there, so leaving the form up
          // invites a second Create that mints a duplicate artist. They can
          // re-add it through "Existing vendor".
          await onDone();
          close();
          return;
        }
        // Nothing was created — the form is still safe to retry as-is.
        toast.error(errorMessage(error));
        await onDone();
        return;
      }

      // A menu is where items live. Merch Manager creates one lazily the first
      // time somebody adds an item (EnsureMenuForLocationAsync), but doing it
      // here means the location is ready the moment it's set up — and Merch Manager
      // finds and REUSES this menu rather than making a second one, because it
      // looks up an existing menu for the location+vendor first.
      //
      // Shape matches EnsureMenuForLocationAsync exactly so the two paths can't
      // diverge. Non-fatal: the artist is already on the event, and the lazy
      // path still covers us if this fails.
      if (newLocationId !== null) {
        try {
          const menu = await client.mutate<{
            insert_menus_one: { id: number } | null;
          }>({
            mutation: CREATE_MERCH_MENU,
            variables: {
              object: merchMenuInput(
                eventId,
                vendorId,
                `${vendorLabel || "Merch"} — Merch`
              ),
            },
          });
          const menuId = menu.data?.insert_menus_one?.id;
          if (menuId) {
            await client.mutate({
              mutation: CREATE_MERCH_LOCATION_MENU,
              variables: { object: { menu_id: menuId, location_id: newLocationId } },
            });
          }
        } catch (error) {
          toast.error(
            `Added to the event, but the menu wasn't created: ${errorMessage(
              error
            )} Merch Manager will create one when the first item is added.`
          );
        }
      }

      toast.success(
        tab === "new" ? "Artist added to this event" : "Vendor added to this event"
      );
      await onDone();
      close();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MerchModal
      open={open}
      busy={saving}
      onClose={close}
      title={tab === "new" ? "Add artist" : "Add existing vendor"}
      description="Artists appear on this tab once they have an active location at this event, so this creates that location too."
      footer={
        // Source order is [primary, cancel]; MerchModal's footer is
        // flex-row-reverse, so this renders as  Cancel | Create Artist.
        <>
          <PrimaryButton
            onClick={() => {
              void handleSubmit();
            }}
            isDisabled={!valid || saving}
            isLoading={saving}
          >
            {tab === "new" ? "Create Artist" : "Add To Event"}
          </PrimaryButton>
          <TertiaryButton onClick={close} isDisabled={saving}>
            Cancel
          </TertiaryButton>
        </>
      }
    >
      <Segmented
        value={tab}
        disabled={saving}
        onChange={(value) => {
          setTab(value === "existing" ? "existing" : "new");
          setLocationNameTouched(false);
        }}
        options={[
          { value: "new", label: "New artist" },
          { value: "existing", label: "Existing vendor" },
        ]}
      />

      <div className="mt-4 grid grid-cols-6 gap-4">
        {tab === "new" ? (
          <>
            <div className="col-span-6" key={`artist-name-${formKey}`}>
              <Input
                label="Name"
                name=""
                value={name}
                placeholder="Artist or band name"
                onChange={(value) => setName(String(value ?? ""))}
              />
            </div>
            <div className="col-span-6" key={`artist-email-${formKey}`}>
              <Input
                label="Contact email (optional)"
                name=""
                type="email"
                value={email}
                placeholder="merch@artist.com"
                onChange={(value) => setEmail(String(value ?? ""))}
              />
            </div>
          </>
        ) : (
          <div className="col-span-6">
            <CustomSelect
              title="Vendor"
              isDisabled={saving}
              value={existingVendorId}
              onChange={(value) => {
                setExistingVendorId(Number(value ?? 0));
                setLocationNameTouched(false);
              }}
              options={[
                { value: 0, label: "Select a vendor…" },
                ...availableVendors.map((vendor) => ({
                  value: vendor.id,
                  label: vendor.name,
                })),
              ]}
            />
            {availableVendors.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Every active vendor in this organization is already at this
                event.
              </p>
            )}
          </div>
        )}

        {locationTypes.length > 0 && (
          <div className="col-span-6">
            <CustomSelect
              title="Location type"
              isDisabled={saving}
              value={locationType}
              onChange={(value) => setLocationType(String(value ?? ""))}
              options={locationTypes}
            />
          </div>
        )}

        <div className="col-span-6" key={`location-name-${formKey}`}>
          <Input
            label="Location name"
            name=""
            value={locationValue}
            placeholder="Merch booth"
            onChange={(value) => {
              setLocationNameTouched(true);
              setLocationName(String(value ?? ""));
            }}
          />
          <p className="mt-1 text-xs text-gray-400">
            This is the selling location Merch Manager reports against. Defaults
            to the vendor's name plus "Merch".
          </p>
        </div>
      </div>

      {tab === "new" && !organizationId && (
        <p className="mt-3 text-xs text-red-600">
          This event has no organization, so a new vendor can't be created here.
        </p>
      )}
    </MerchModal>
  );
};

/** Create a vendor manager and assign them to this event in one go. */
const AddManagerModal = ({
  open,
  eventId,
  organizationId,
  onClose,
  onDone,
}: {
  open: boolean;
  eventId: number;
  organizationId: number | null | undefined;
  onClose: () => void;
  onDone: () => Promise<void>;
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const valid = name.trim() !== "" && Boolean(organizationId);

  const reset = () => {
    setName("");
    setEmail("");
    setFormKey((key) => key + 1);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    let createdName = "";
    try {
      const created = await client.mutate<{
        insert_vendors_one: { id: number } | null;
      }>({
        mutation: CREATE_MERCH_VENDOR,
        variables: {
          object: {
            name: name.trim(),
            // vendors.type is not nullable and its enum has no "manager"
            // member — MERCHANDISE keeps them in the merch family without
            // inventing a value the rest of the app doesn't understand.
            // is_merch_vendor_manager is the flag that actually identifies them.
            type: VendorType.MERCHANDISE,
            organization_id: organizationId,
            poc_email: email.trim() === "" ? null : email.trim(),
            is_active: true,
            is_merch_vendor_manager: true,
            last_updated_by: currentUserId(),
          },
        },
      });
      const vendorId = created.data?.insert_vendors_one?.id;
      if (!vendorId) throw new Error("The vendor manager wasn't created.");
      createdName = name.trim();

      await client.mutate({
        mutation: UPSERT_MERCH_EVENT_MANAGER,
        variables: {
          object: {
            event_id: eventId,
            vendor_id: vendorId,
            last_updated_by: currentUserId(),
          },
        },
      });

      toast.success("Vendor manager added to this event");
      await onDone();
      close();
    } catch (error) {
      // Same half-done problem as the artist modal: if the assignment failed
      // after the vendor was created, the manager exists org-wide but won't be
      // offered on this event's picker. Name it.
      if (createdName !== "") {
        toast.error(
          `Created "${createdName}", but couldn't assign them to this event: ${errorMessage(
            error
          )} They won't appear in the vendor manager list yet.`
        );
        await onDone();
      } else {
        toast.error(errorMessage(error));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <MerchModal
      open={open}
      busy={saving}
      onClose={close}
      title="Add vendor manager"
      description="Vendor managers get their own vendor row — that's where their statement and connected account hang. Adding one here assigns them to this event."
      footer={
        <>
          <PrimaryButton
            onClick={() => {
              void handleSubmit();
            }}
            isDisabled={!valid || saving}
            isLoading={saving}
          >
            Create Manager
          </PrimaryButton>
          <TertiaryButton onClick={close} isDisabled={saving}>
            Cancel
          </TertiaryButton>
        </>
      }
    >
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-6" key={`manager-name-${formKey}`}>
          <Input
            label="Name"
            name=""
            value={name}
            placeholder="Management company or manager"
            onChange={(value) => setName(String(value ?? ""))}
          />
        </div>
        <div className="col-span-6" key={`manager-email-${formKey}`}>
          <Input
            label="Contact email (optional)"
            name=""
            type="email"
            value={email}
            placeholder="manager@example.com"
            onChange={(value) => setEmail(String(value ?? ""))}
          />
        </div>
      </div>
      {!organizationId && (
        <p className="mt-3 text-xs text-red-600">
          This event has no organization, so a vendor manager can't be created
          here.
        </p>
      )}
    </MerchModal>
  );
};

/**
 * Send somebody a Merch Manager login for this event.
 *
 * Mounted ONCE by MerchVendorDeals and re-targeted, so the email field resets
 * every time it opens on a different vendor. Nothing is written to Hasura here
 * — ronin-api owns the invite row and the email, and this UI never reads it
 * back (see the merchInvite section above).
 */
const InviteModal = ({
  open,
  eventId,
  target,
  alreadySent,
  onClose,
  onSent,
}: {
  open: boolean;
  eventId: number;
  target: InviteTarget | null;
  /** Sent to this same vendor+role earlier in this page visit. */
  alreadySent: boolean;
  onClose: () => void;
  onSent: (target: InviteTarget) => void;
}) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const targetKey = target ? inviteKey(target.role, target.vendorId) : "";
  const prefill = target?.email ?? "";

  useEffect(() => {
    if (!open) return;
    setEmail(prefill);
    // Input keeps its own internal state off defaultValue — remount it so a
    // previous invitee's address can't linger in the box.
    setFormKey((key) => key + 1);
  }, [open, targetKey, prefill]);

  // Not a validator, just a guard against an obvious typo reaching the API —
  // ronin-api is the authority on what it will accept.
  const valid = /\S+@\S+\.\S+/.test(email.trim());

  const close = () => {
    setEmail("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!target || !valid || sending) return;
    setSending(true);
    try {
      const result = await sendMerchInvite({
        eventId,
        email: email.trim(),
        role: target.role,
        vendorId: target.vendorId,
      });
      const who = result.email || email.trim();
      // Two genuinely different outcomes: one person gets an email to act on,
      // the other already has an account and just got access. Saying "invite
      // sent" to the second is a support ticket waiting to happen.
      if (result.existingUser) {
        toast.success(
          `${who} already has a Ronin account — they've been told they now have access.`
        );
      } else {
        toast.success(`Invitation sent to ${who}.`);
      }
      onSent(target);
      close();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSending(false);
    }
  };

  const roleLabel = target ? INVITE_ROLE_LABEL[target.role] : "";

  return (
    <MerchModal
      open={open}
      busy={sending}
      onClose={close}
      title={alreadySent ? "Resend invite" : "Send invite"}
      description={
        target
          ? `Emails ${target.vendorName} a link to Merch Manager for this event as the ${roleLabel}. Sending again replaces any earlier link.`
          : undefined
      }
      footer={
        // Source order is [primary, cancel]; MerchModal's footer is
        // flex-row-reverse, so this renders as  Cancel | Send Invite.
        <>
          <PrimaryButton
            onClick={() => {
              void handleSubmit();
            }}
            isDisabled={!valid || sending || !target}
            isLoading={sending}
          >
            {alreadySent ? "Resend Invite" : "Send Invite"}
          </PrimaryButton>
          <TertiaryButton onClick={close} isDisabled={sending}>
            Cancel
          </TertiaryButton>
        </>
      }
    >
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-6" key={`invite-email-${formKey}`}>
          <Input
            label="Email"
            name=""
            type="email"
            value={email}
            placeholder="them@example.com"
            onChange={(value) => setEmail(String(value ?? ""))}
          />
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        Invite status isn't shown here — this sends the email and doesn't track
        it. If in doubt, send it again.
      </p>
    </MerchModal>
  );
};

// ---------------------------------------------------------------------------
// locations & menus
//
// locations and menus are both (event_id, vendor_id); location_menu (SINGULAR)
// is the join between them, and it is MANY-to-MANY in both directions: one menu
// can serve several locations, and a shared location can carry a food menu AND a merch
// menu. Everything below therefore attaches and detaches individual JOIN ROWS
// by id — there is deliberately no "set the menus on this location" path, which
// would silently detach another module's menu.
//
// Nothing here HARD-deletes, for either kind of row. A menu is detached from a
// location by removing the join row (the menu itself survives on every other location
// it serves), and a location is removed by clearing locations.is_active — never a
// DELETE, which would either fail on a foreign key from orders / counts or take
// history with it. And because an active location is what ties the artist to
// this event, removing their LAST one drops the artist off this tab: that path
// exists (RemoveLocationModal) but it says so first, in those words.
// ---------------------------------------------------------------------------

/** The rename/edit affordance. Same glyph everywhere so "you can change this"
 *  is one visual, not three. */
const PencilGlyph = () => (
  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

/** Pencil → input → Save. `onSave` resolves false to keep the editor open so a
 *  failed write doesn't discard what was typed. */
const InlineRename = ({
  value,
  title,
  disabled,
  onSave,
}: {
  value: string;
  title: string;
  disabled?: boolean;
  onSave: (name: string) => Promise<boolean>;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const next = draft.trim();
    if (next === "" || next === value.trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate">{value}</span>
        {!disabled && (
          <button
            type="button"
            title={title}
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            className="shrink-0 p-0.5 text-gray-300 hover:text-gray-500"
          >
            <PencilGlyph />
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1">
      {/* Raw input with NO name: this renders inside the event's react-router
          <Form>, and any named field is spread into the events _set mutation. */}
      <input
        type="text"
        value={draft}
        autoFocus
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            // Stop the surrounding form from submitting on Enter.
            event.preventDefault();
            void commit();
          }
          if (event.key === "Escape") setEditing(false);
        }}
        className="block w-56 max-w-full rounded-md border-gray-300 py-1 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50"
      />
      <button
        type="button"
        disabled={saving}
        onClick={() => {
          void commit();
        }}
        className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40"
      >
        Save
      </button>
      <button
        type="button"
        disabled={saving}
        onClick={() => setEditing(false)}
        className="shrink-0 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
      >
        Cancel
      </button>
    </span>
  );
};

/**
 * InlineRename's interaction, for a value that comes from a fixed list instead
 * of the keyboard: text + pencil, then picker + Save/Cancel in place. Same
 * idiom on purpose — the location card already teaches "pencil means you can change
 * this", and a second editing gesture on the same card would have to be learned
 * separately.
 *
 * Deliberately a native <select> rather than CustomSelect: this sits on the
 * chip line of a card header, and react-select's bordered control is several
 * times its height. NO `name` attribute, like every other field in this file —
 * see the header.
 */
const InlineSelect = ({
  value,
  options,
  emptyLabel,
  title,
  disabled,
  onSave,
}: {
  value: string;
  options: { value: string; label: string }[];
  /** Shown when nothing is set yet. */
  emptyLabel: string;
  title: string;
  disabled?: boolean;
  onSave: (value: string) => Promise<boolean>;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  // A stored value the org's reference table doesn't offer (renamed, retired,
  // or set by another app) must still be READABLE, and must not be silently
  // rewritten to the first option just because somebody opened the picker.
  const known = options.find((option) => option.value === value);

  const commit = async () => {
    if (draft === "" || draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        <span
          className={`truncate text-[11px] uppercase tracking-wide ${
            value === "" ? "italic text-gray-300" : "text-gray-400"
          }`}
        >
          {value === "" ? emptyLabel : known?.label ?? value}
        </span>
        {!disabled && options.length > 0 && (
          <button
            type="button"
            title={title}
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            className="shrink-0 p-0.5 text-gray-300 hover:text-gray-500"
          >
            <PencilGlyph />
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1">
      {/* Raw select with NO name — see the file header. */}
      <select
        value={draft}
        autoFocus
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            // Stop the surrounding form from submitting on Enter.
            event.preventDefault();
            void commit();
          }
          if (event.key === "Escape") setEditing(false);
        }}
        className="block w-48 max-w-full rounded-md border-gray-300 py-1 text-xs focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50"
      >
        {value === "" && <option value="">{emptyLabel}</option>}
        {/* The current value, when the reference list no longer carries it. */}
        {value !== "" && !known && <option value={value}>{value}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={saving}
        onClick={() => {
          void commit();
        }}
        className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40"
      >
        Save
      </button>
      <button
        type="button"
        disabled={saving}
        onClick={() => setEditing(false)}
        className="shrink-0 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
      >
        Cancel
      </button>
    </span>
  );
};

/**
 * Another location for an artist who is already on this event — either a brand new
 * location, or one that ALREADY EXISTS at this event.
 *
 * "New location" is the same two writes as the artist modal's second half —
 * location, then optionally a menu attached to it — minus the vendor.
 *
 * "Existing location" is a single write to locations.vendor_id, and it is the
 * dangerous one. That column is the location's OWNER, not a label: re-pointing it
 * MOVES the location off whoever holds it, and Merch Manager's counts
 * (inventory_count_line, keyed on location_id) move with it. So every option in
 * the picker names its current owner, and taking one off another vendor needs
 * an explicit confirmation that names BOTH of them. A location with no vendor
 * at all is nobody's, so that one goes through without a prompt.
 */
const AddLocationModal = ({
  open,
  eventId,
  artist,
  organizationId,
  existingMenus,
  onClose,
  onDone,
  onOwnerChanged,
}: {
  open: boolean;
  eventId: number;
  artist: MerchArtist;
  organizationId: number | null | undefined;
  /** This vendor's menus at this event — only used to pick a non-colliding name. */
  existingMenus: SetupMenu[];
  onClose: () => void;
  onDone: () => Promise<void>;
  /** Called ONLY when a location was taken off another vendor: that vendor's own
   *  panel (and possibly their presence on this tab at all) is now stale, and
   *  only the section above this can reload the artist list. */
  onOwnerChanged?: () => void;
}) => {
  const [tab, setTab] = useState<"new" | "existing">("new");
  const [name, setName] = useState("");
  const [withMenu, setWithMenu] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [pool, setPool] = useState<PoolLocation[]>([]);
  const [poolLoaded, setPoolLoaded] = useState(false);
  const [poolError, setPoolError] = useState<string | null>(null);
  const [pickedLocationId, setPickedLocationId] = useState(0);
  const { locationTypes, locationType, setLocationType } = useLocationTypes(
    open,
    organizationId
  );

  // Every active location at this event, loaded only when the tab is actually
  // showing. network-only: somebody else may have moved a location since this page
  // loaded, and a stale owner name in a confirmation prompt is the one thing
  // this dialog must never show.
  useEffect(() => {
    if (!open || tab !== "existing") return;
    let cancelled = false;
    setPoolLoaded(false);
    void (async () => {
      try {
        const { data } = await client.query<LocationPoolQueryResult>({
          query: GET_EVENT_LOCATION_POOL,
          variables: { eventId },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        setPool(data?.locations ?? []);
        setPoolError(null);
      } catch (err) {
        if (cancelled) return;
        setPool([]);
        setPoolError(errorMessage(err));
      } finally {
        if (!cancelled) setPoolLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab, eventId]);

  const poolName = (location: PoolLocation): string =>
    location.name?.trim() || `Location ${location.id}`;

  /** The vendor holding this location right now, "" if it has none. */
  const ownerName = (location: PoolLocation): string => {
    if (location.vendor_id === null) return "";
    return location.vendor?.name?.trim() || `Vendor ${location.vendor_id}`;
  };

  /** Locations at this event that aren't already this artist's. Theirs are
   *  filtered out because re-pointing a location at its current owner is a no-op. */
  const candidates = useMemo(
    () => pool.filter((location) => location.vendor_id !== artist.id),
    [pool, artist.id]
  );

  const picked = candidates.find((location) => location.id === pickedLocationId);
  const pickedOwner = picked ? ownerName(picked) : "";
  /** True when accepting this would take the location OFF somebody. */
  const isMove = Boolean(picked && picked.vendor_id !== null);

  const needsLocationType = locationTypes.length > 0 && locationType === "";
  const valid =
    tab === "new"
      ? name.trim() !== "" && !needsLocationType
      : pickedLocationId !== 0 && Boolean(picked);

  const close = () => {
    setTab("new");
    setName("");
    setWithMenu(true);
    setPickedLocationId(0);
    setFormKey((key) => key + 1);
    onClose();
  };

  /** Hand an existing location to this artist. */
  const handleAttachExisting = async () => {
    if (!picked || saving) return;

    // Never reassign silently: name both vendors and say where the counts go.
    if (isMove) {
      const confirmed = window.confirm(
        `Move "${poolName(picked)}" from "${pickedOwner}" to "${artist.name}"?\n\n` +
          "Any counts recorded at this location go with it. " +
          `"${pickedOwner}" will no longer have this location at this event.`
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      const result = await client.mutate<{
        update_locations_by_pk: { id: number } | null;
      }>({
        mutation: REASSIGN_MERCH_LOCATION,
        variables: {
          id: picked.id,
          input: { vendor_id: artist.id, last_updated_by: currentUserId() },
        },
      });
      // by_pk returns null rather than erroring when the row is gone or a
      // permission blocked the write.
      if (!result.data?.update_locations_by_pk) {
        throw new Error(
          "That location wasn't moved — it may have been changed or removed since this list loaded."
        );
      }
      toast.success(
        isMove
          ? `"${poolName(picked)}" moved from ${pickedOwner} to ${artist.name}`
          : `"${poolName(picked)}" is now ${artist.name}'s`
      );
      await onDone();
      const movedFromSomebody = isMove;
      close();
      // After close, so the reload can't yank this dialog out mid-write.
      if (movedFromSomebody) onOwnerChanged?.();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!valid || saving) return;
    if (tab === "existing") {
      await handleAttachExisting();
      return;
    }
    setSaving(true);
    let locationId: number | null = null;
    try {
      const created = await client.mutate<{
        insert_locations_one: { id: number } | null;
      }>({
        mutation: CREATE_MERCH_LOCATION,
        variables: {
          object: {
            name: name.trim(),
            event_id: eventId,
            vendor_id: artist.id,
            ...(locationType === "" ? {} : { location_type: locationType }),
            is_active: true,
            last_updated_by: currentUserId(),
          },
        },
      });
      locationId = created.data?.insert_locations_one?.id ?? null;
      if (locationId === null) throw new Error("The location wasn't created.");
    } catch (error) {
      toast.error(errorMessage(error));
      setSaving(false);
      return;
    }

    if (withMenu) {
      try {
        const menu = await client.mutate<{
          insert_menus_one: { id: number } | null;
        }>({
          mutation: CREATE_MERCH_MENU,
          variables: {
            object: merchMenuInput(
              eventId,
              artist.id,
              nextMerchMenuName(artist.name, existingMenus)
            ),
          },
        });
        const menuId = menu.data?.insert_menus_one?.id;
        if (!menuId) throw new Error("The menu wasn't created.");
        await client.mutate({
          mutation: CREATE_MERCH_LOCATION_MENU,
          variables: { object: { menu_id: menuId, location_id: locationId } },
        });
        toast.success("Location and menu added");
      } catch (error) {
        // The location exists but has nowhere to put items — say so rather than
        // reporting a flat failure for a half-done write.
        toast.error(
          `Added the location, but the menu wasn't set up: ${errorMessage(
            error
          )} Attach or create one from the list.`
        );
      }
    } else {
      toast.success("Location added");
    }

    await onDone();
    setSaving(false);
    close();
  };

  return (
    <MerchModal
      open={open}
      busy={saving}
      onClose={close}
      title={tab === "new" ? "Add location" : "Use an existing location"}
      description={
        tab === "new"
          ? `Another selling location for ${artist.name} at this event. Merch Manager reports against each location separately.`
          : `Give ${artist.name} a location that is already set up at this event, instead of creating a second one next to it.`
      }
      footer={
        // [primary, cancel] + MerchModal's flex-row-reverse = Cancel | primary.
        <>
          <PrimaryButton
            onClick={() => {
              void handleSubmit();
            }}
            isDisabled={!valid || saving}
            isLoading={saving}
          >
            {tab === "new"
              ? "Add Location"
              : isMove
              ? "Move Location"
              : "Use Location"}
          </PrimaryButton>
          <TertiaryButton onClick={close} isDisabled={saving}>
            Cancel
          </TertiaryButton>
        </>
      }
    >
      <Segmented
        value={tab}
        disabled={saving}
        onChange={(value) => {
          setTab(value === "existing" ? "existing" : "new");
          setPickedLocationId(0);
        }}
        options={[
          { value: "new", label: "New location" },
          { value: "existing", label: "Existing location" },
        ]}
      />

      {tab === "new" ? (
        <div className="mt-4 grid grid-cols-6 gap-4">
          <div className="col-span-6" key={`new-location-name-${formKey}`}>
            <Input
              label="Location name"
              name=""
              value={name}
              placeholder={`${artist.name} Merch`}
              onChange={(value) => setName(String(value ?? ""))}
            />
          </div>
          {locationTypes.length > 0 && (
            <div className="col-span-6">
              <CustomSelect
                title="Location type"
                isDisabled={saving}
                value={locationType}
                onChange={(value) => setLocationType(String(value ?? ""))}
                options={locationTypes}
              />
            </div>
          )}
          <div className="col-span-6">
            <label className="flex items-start gap-2">
              {/* No name — see the file header. */}
              <input
                type="checkbox"
                checked={withMenu}
                disabled={saving}
                onChange={(event) => setWithMenu(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Also create a merch menu and attach it
                <span className="block text-xs text-gray-400">
                  Items can't be added to a location with no menu. Leave this off
                  only if you're going to attach one of this artist's existing
                  menus.
                </span>
              </span>
            </label>
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-6 gap-4">
          <div className="col-span-6">
            {/* No `name` on CustomSelect — see the file header. */}
            <CustomSelect
              title="Location at this event"
              isDisabled={saving || !poolLoaded || candidates.length === 0}
              value={pickedLocationId}
              onChange={(value) => setPickedLocationId(Number(value ?? 0))}
              options={[
                { value: 0, label: "Select a location…" },
                ...candidates.map((location) => ({
                  value: location.id,
                  // The owner is part of the label on purpose: picking blind is
                  // how somebody quietly walks off with another vendor's location.
                  label: `${poolName(location)} — ${
                    ownerName(location)
                      ? `currently ${ownerName(location)}`
                      : "no vendor yet"
                  }`,
                })),
              ]}
            />
            {!poolLoaded && !poolError && (
              <p className="mt-1 text-xs text-gray-400">
                Loading this event's locations…
              </p>
            )}
            {poolError && (
              <p className="mt-1 text-xs text-red-600">
                Couldn't load this event's locations. {poolError}
              </p>
            )}
            {poolLoaded && !poolError && candidates.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Every active location at this event already belongs to{" "}
                {artist.name}.
              </p>
            )}
          </div>

          {picked && isMove && (
            <div className="col-span-6 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2">
              <p className="text-sm font-medium text-yellow-800">
                "{poolName(picked)}" belongs to {pickedOwner}.
              </p>
              <p className="mt-0.5 text-xs text-yellow-700">
                Continuing MOVES the location to {artist.name} — {pickedOwner} will
                no longer have it at this event, and any counts recorded at it go
                with it. You'll be asked to confirm.
              </p>
            </div>
          )}
          {picked && !isMove && (
            <p className="col-span-6 text-xs text-gray-500">
              "{poolName(picked)}" has no vendor on it, so nothing is taken off
              anyone — it just becomes {artist.name}'s.
            </p>
          )}

          <p className="col-span-6 text-xs text-gray-400">
            Menus already attached to the location stay attached. Add or swap
            them from the location once it's here.
          </p>
        </div>
      )}
    </MerchModal>
  );
};

/**
 * Take a location off this event — by clearing is_active, never by deleting.
 *
 * A dialog rather than a window.confirm (which is what a menu Detach still
 * uses) because there are two consequences that a one-line prompt can't carry
 * honestly:
 *
 *   1. Counts. Merch Manager's inventory_count_line rows hang off location_id.
 *      They are NOT deleted and still back any settlement already computed, but
 *      they stop being reachable from either UI — so the number is fetched
 *      BEFORE the write and shown while the user can still say no. If that
 *      query fails (the table isn't tracked in every environment) the answer is
 *      "unknown", which is said out loud and does NOT block the removal.
 *   2. The artist. This tab's list is derived from active locations, so
 *      removing the last one removes the ARTIST from the event — that gets its
 *      own, louder warning and its own acknowledgement.
 */
/**
 * Which item categories Inventory Manager may use at this event.
 *
 * CLOSED BY DEFAULT, on purpose: with nothing selected the app offers no
 * categories and refuses to create items, telling the user to come here. That
 * is ops' call — a merch tent shouldn't scroll past forty food-service
 * categories, and a silently-wrong category is worse than a blocked save,
 * because `items.category` has to round-trip back to the admin app's own
 * dropdown (an unrecognised value shows blank over there).
 *
 * SAVES ON CHANGE, like the fee lines and locations beside it — no button, and
 * no `name` attributes (which would be swept into the event form's FormData and
 * sent to the events _set mutation as a column that doesn't exist).
 *
 * Writes go through UPDATE_EVENT_MERCH_SETTINGS `_append`, which merges these
 * two keys into the configuration JSONB and leaves every other module's keys
 * (order_ahead_slug, checkout_field_label…) intact.
 *
 * Stored on events.configuration as:
 *   merch_item_categories       string[]  reference_value, order-insensitive
 *   merch_default_item_category string    pre-selected when adding an item
 */
const ItemCategoryPicker = ({
  eventId,
  organizationId,
}: {
  eventId: number;
  /** Nullable everywhere in this file — an event can load before its org does. */
  organizationId: number | null | undefined;
}) => {
  const [catalog, setCatalog] = useState<
    { value: string; label: string }[] | null
  >(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fallback, setFallback] = useState<string>("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [cats, cfg] = await Promise.all([
        client.query<{
          dashboard_reference: {
            reference_value: string | null;
            reference_label: string | null;
          }[];
        }>({
          query: GET_ORG_ITEM_CATEGORIES,
          // ?? 0 matches the rest of this file: 0 is the GLOBAL/shared
          // reference org, so a missing org still returns Ronin's shared
          // categories instead of erroring on a null variable.
          variables: { organizationId: organizationId ?? 0 },
          fetchPolicy: "network-only",
        }),
        client.query<{
          events_by_pk: { configuration: Record<string, unknown> | null } | null;
        }>({
          query: GET_EVENT_MERCH_CONFIG,
          variables: { eventId },
          fetchPolicy: "network-only",
        }),
      ]);

      setCatalog(
        (cats.data?.dashboard_reference ?? [])
          .filter((r) => (r.reference_value ?? "").trim().length > 0)
          .map((r) => ({
            value: r.reference_value as string,
            label: r.reference_label || (r.reference_value as string),
          }))
      );

      const configuration = cfg.data?.events_by_pk?.configuration ?? {};
      const saved = configuration["merch_item_categories"];
      setSelected(
        new Set(
          Array.isArray(saved)
            ? saved.filter((v): v is string => typeof v === "string")
            : []
        )
      );
      const savedDefault = configuration["merch_default_item_category"];
      setFallback(typeof savedDefault === "string" ? savedDefault : "");
      setLoadError(null);
    } catch (error) {
      setLoadError(errorMessage(error));
    }
  }, [eventId, organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Persist immediately. Takes the next state EXPLICITLY rather than reading it
   * back from useState — a handler that setState()s and then saves would write
   * the PREVIOUS values, because the variable it closed over hasn't updated
   * yet. That's the classic stale-closure autosave bug, and it presents as "the
   * last thing I ticked never saves".
   */
  const persist = useCallback(
    async (nextValues: string[], nextDefault: string) => {
      // Never store a default outside the selection — Inventory Manager drops
      // it on read and the pre-fill silently stops working.
      const chosen = nextValues.includes(nextDefault)
        ? nextDefault
        : nextValues[0] ?? "";
      setSaving(true);
      try {
        await client.mutate({
          mutation: UPDATE_EVENT_MERCH_SETTINGS,
          variables: {
            eventId,
            patch: {
              merch_item_categories: nextValues,
              merch_default_item_category: chosen,
            },
          },
        });
        if (chosen !== nextDefault) setFallback(chosen);
        setSavedAt(Date.now());
      } catch (error) {
        toast.error(errorMessage(error));
        // Put the UI back to what's actually stored, rather than leaving a tick
        // on screen that never reached the database.
        await load();
      } finally {
        setSaving(false);
      }
    },
    [eventId, load]
  );

  const toggle = (value: string) => {
    const next = new Set(selected);
    let nextDefault = fallback;
    if (next.has(value)) {
      next.delete(value);
      // Un-ticking the default clears it too, otherwise we'd store a default
      // that isn't in the allowlist.
      if (fallback === value) nextDefault = "";
    } else {
      next.add(value);
    }
    setSelected(next);
    setFallback(nextDefault);
    void persist(Array.from(next), nextDefault);
  };

  const chooseDefault = (value: string) => {
    setFallback(value);
    void persist(Array.from(selected), value);
  };

  return (
    <div className="mb-4 rounded-md border border-gray-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">Item categories</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Which categories Inventory Manager offers when someone adds an item
            at this event. Tick the ones that apply, and star one to pre-select
            it. Until at least one is ticked, items can't be added — the app
            sends people here. Changes save on their own.
          </p>
        </div>
        <div className="flex shrink-0 items-center text-xs text-gray-400">
          {saving ? "Saving…" : savedAt !== null ? "Saved" : null}
        </div>
      </div>

      {catalog === null && !loadError && (
        <p className="mt-3 text-sm text-gray-400">Loading categories…</p>
      )}

      {loadError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-700">
            Couldn't load the category list. {loadError}
          </p>
          <div className="mt-2 flex justify-end">
            <TertiaryButton onClick={() => void load()}>Retry</TertiaryButton>
          </div>
        </div>
      )}

      {catalog !== null && catalog.length === 0 && !loadError && (
        <p className="mt-3 text-sm text-gray-500">
          This organization has no item categories set up yet. Add them under
          Dashboard References before configuring this event.
        </p>
      )}

      {catalog !== null && catalog.length > 0 && (
        <>
          {selected.size === 0 && (
            <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2">
              <p className="text-sm text-yellow-800">
                Nothing selected — Inventory Manager will refuse to add items at
                this event until you tick at least one.
              </p>
            </div>
          )}
          <div className="mt-3 grid grid-cols-1 gap-1 sm:grid-cols-2">
            {catalog.map((c) => {
              const on = selected.has(c.value);
              const isDefault = fallback === c.value;
              return (
                <div
                  key={c.value}
                  className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-gray-50"
                >
                  <label className="flex min-w-0 items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      // NO `name` — it would be swept into the event form's
                      // FormData and sent as an events column.
                      checked={on}
                      disabled={saving}
                      onChange={() => toggle(c.value)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="truncate">{c.label}</span>
                  </label>
                  {/* Only a ticked category can be the default. */}
                  {on && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => chooseDefault(c.value)}
                      title={
                        isDefault
                          ? "Pre-selected when adding an item"
                          : "Make this the pre-selected category"
                      }
                      className={`shrink-0 text-xs font-medium ${
                        isDefault
                          ? "text-primary-600"
                          : "text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      {isDefault ? "★ Default" : "☆ Default"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Take an ARTIST off this event's merch configuration.
 *
 * There is no events↔vendors link to cut: the artist is on this tab because
 * they own an active LOCATION here. So this deactivates every active location
 * they own at the event, then flags their deal inactive. Both are soft — see
 * DEACTIVATE_MERCH_VENDOR_LOCATIONS.
 *
 * BLOCKS rather than warns. Removing an artist can take several locations at
 * once, so unlike the single-location dialog (which lets you acknowledge and
 * proceed) this refuses outright the moment anything real exists: a count
 * session, a counted line, an order, or a settlement row. Ops removes the
 * blocker deliberately — reassign the location, or handle the settlement —
 * rather than ticking a box on a bulk action.
 */
const RemoveArtistModal = ({
  open,
  eventId,
  artist,
  onClose,
  onRemoved,
}: {
  open: boolean;
  eventId: number;
  /** null between closing and unmounting — the dialog animates out. */
  artist: MerchArtist | null;
  onClose: () => void;
  onRemoved: () => Promise<void>;
}) => {
  const [usage, setUsage] = useState<{
    /** Counted fresh here — the accordion header doesn't track it. */
    locations: number;
    sessions: number | null;
    lines: number | null;
    orders: number | null;
    settlementStatus: string | null;
    settlementLocked: boolean;
    hasSettlement: boolean;
  } | null>(null);
  const [checked, setChecked] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const vendorId = artist?.id ?? null;

  // Keep the last live artist so the copy doesn't flash empty on the way out.
  const shown = useRef({ name: artist?.name ?? "" });
  if (artist) shown.current = { name: artist.name };
  const shownName = artist ? artist.name : shown.current.name;
  const shownCount = usage?.locations ?? 0;

  useEffect(() => {
    if (!open || vendorId === null) return;
    let cancelled = false;
    setChecked(false);
    setCheckFailed(false);
    setUsage(null);
    void (async () => {
      try {
        // The ids are read fresh rather than taken from props: the panel's list
        // may be stale, and this is the query that decides whether money-shaped
        // history exists.
        const setup = await client.query<{
          locations: { id: number }[];
        }>({
          query: GET_MERCH_VENDOR_SETUP,
          variables: { eventId, vendorId },
          fetchPolicy: "network-only",
        });
        const ids = (setup.data?.locations ?? []).map((l) => l.id);

        const { data } = await client.query<{
          inventory_count_session_aggregate?: { aggregate?: { count?: number } };
          inventory_count_line_aggregate?: { aggregate?: { count?: number } };
          orders_aggregate?: { aggregate?: { count?: number } };
          vendor_settlement?: {
            id: number;
            status: string | null;
            locked_at: string | null;
          }[];
        }>({
          query: GET_MERCH_ARTIST_USAGE,
          variables: {
            eventId,
            vendorId,
            // _in: [] matches NOTHING and would report a reassuring zero.
            locationIds: ids.length > 0 ? ids : [-1],
          },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        // A vendor should only ever have one settlement per event, but the list
        // is unordered — reading [0] would let a draft row mask a LOCKED one and
        // describe a signed settlement as merely "started".
        const settlements = data?.vendor_settlement ?? [];
        // typeof, not `?? null`: an aggregate that came back undefined is
        // UNKNOWN, and a coalesce to 0 would read as "nothing recorded".
        const num = (value: unknown) => (typeof value === "number" ? value : null);
        setUsage({
          locations: ids.length,
          sessions: num(data?.inventory_count_session_aggregate?.aggregate?.count),
          lines: num(data?.inventory_count_line_aggregate?.aggregate?.count),
          orders: num(data?.orders_aggregate?.aggregate?.count),
          settlementStatus: settlements[0]?.status ?? null,
          settlementLocked: settlements.some((s) => Boolean(s.locked_at)),
          hasSettlement: settlements.length > 0,
        });
      } catch {
        // Unknown is NOT none. A failed check must not read as "safe to remove".
        if (!cancelled) setCheckFailed(true);
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, eventId, vendorId]);

  /** Every reason this artist can't be removed, in the user's words. */
  const blockers: string[] = [];
  if (usage) {
    if (usage.settlementLocked) {
      blockers.push(
        "Their settlement is locked and signed — those numbers are a financial record."
      );
    } else if (usage.hasSettlement) {
      blockers.push(
        `A settlement has been started for them${
          usage.settlementStatus ? ` (${usage.settlementStatus})` : ""
        }. Fees may already be configured against it.`
      );
    }
    if ((usage.orders ?? 0) > 0) {
      blockers.push(
        usage.orders === 1
          ? "1 order has been rung at one of their locations."
          : `${usage.orders} orders have been rung at their locations.`
      );
    }
    if ((usage.sessions ?? 0) > 0 || (usage.lines ?? 0) > 0) {
      blockers.push(
        "Merch Manager has counted stock for them at this event."
      );
    }
    // UNKNOWN IS NOT NONE. null here means the aggregate didn't come back, and
    // coalescing it to 0 above would have silently cleared the blocker — the
    // exact inversion of what this dialog is for.
    if (usage.sessions === null || usage.lines === null || usage.orders === null) {
      blockers.push(
        "Couldn't read the full count and sales history for this artist, so removal is blocked rather than guessed at."
      );
    }
    // Nothing to remove: without this the copy below cheerfully offers to
    // deactivate "0 locations" and the button then fails with a message about
    // the list being out of date, which is a worse way to learn the same thing.
    if (usage.locations === 0) {
      blockers.push(
        "They have no active location at this event, so there's nothing to remove — this list is out of date. Reload the tab."
      );
    }
  }
  if (checkFailed) {
    blockers.push(
      "Couldn't check what's already recorded for this artist, so removal is blocked rather than guessed at."
    );
  }

  const canRemove =
    artist !== null && checked && !saving && blockers.length === 0;

  const close = () => {
    if (saving) return;
    onClose();
  };

  const handleRemove = async () => {
    if (!artist || !canRemove) return;
    setSaving(true);
    try {
      const userId = currentUserId();

      // RE-CHECK IMMEDIATELY BEFORE WRITING. The dialog may have sat open for
      // minutes, and the mutation is scoped by (event, vendor) rather than by
      // the ids we validated — so a location created since the first check
      // would be deactivated without ever having been tested for orders. This
      // shrinks the window from minutes to one round trip.
      const fresh = await client.query<{
        inventory_count_session_aggregate?: { aggregate?: { count?: number } };
        inventory_count_line_aggregate?: { aggregate?: { count?: number } };
        orders_aggregate?: { aggregate?: { count?: number } };
        vendor_settlement?: { locked_at: string | null }[];
      }>({
        query: GET_MERCH_ARTIST_USAGE,
        variables: {
          eventId,
          vendorId: artist.id,
          locationIds: (
            await client.query<{ locations: { id: number }[] }>({
              query: GET_MERCH_VENDOR_SETUP,
              variables: { eventId, vendorId: artist.id },
              fetchPolicy: "network-only",
            })
          ).data?.locations?.map((l) => l.id) ?? [-1],
        },
        fetchPolicy: "network-only",
      });
      const stillClear =
        (fresh.data?.inventory_count_session_aggregate?.aggregate?.count ?? 1) === 0 &&
        (fresh.data?.inventory_count_line_aggregate?.aggregate?.count ?? 1) === 0 &&
        (fresh.data?.orders_aggregate?.aggregate?.count ?? 1) === 0 &&
        (fresh.data?.vendor_settlement ?? [{ locked_at: "unknown" }]).length === 0;
      if (!stillClear) {
        throw new Error(
          `Something was recorded for ${artist.name} while this dialog was open — counts, an order or a settlement. Nothing was changed; reopen this to see what.`
        );
      }

      const locResult = await client.mutate<{
        update_locations: { affected_rows: number } | null;
      }>({
        mutation: DEACTIVATE_MERCH_VENDOR_LOCATIONS,
        variables: { eventId, vendorId: artist.id, userId },
      });
      const removed = locResult.data?.update_locations?.affected_rows ?? 0;
      if (removed < 1) {
        throw new Error(
          "Nothing was removed — their locations may have been changed since this list loaded. Reload and try again."
        );
      }

      // Zero here is NORMAL: an artist on the legacy two-party model has no
      // deal row, and we must never insert a placeholder one.
      //
      // Caught SEPARATELY because the locations are already deactivated by this
      // point. Letting it fall to the outer catch would skip onClose/onRemoved,
      // leave the artist visibly on the tab, and report a flat failure — so the
      // obvious retry then hits "nothing was removed" and reads as a second,
      // different bug.
      try {
        await client.mutate({
          mutation: DEACTIVATE_MERCH_VENDOR_DEAL,
          variables: { eventId, vendorId: artist.id, userId },
        });
      } catch (error) {
        toast.error(
          `${artist.name} is off the event — their locations were deactivated — but the saved split wasn't switched off: ${errorMessage(
            error
          )} Harmless: adding them back reuses that split anyway.`
        );
        onClose();
        await onRemoved();
        return;
      }

      toast.success(
        removed === 1
          ? `${artist.name} removed from this event's merch setup`
          : `${artist.name} removed — ${removed} locations deactivated`
      );
      onClose();
      await onRemoved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MerchModal
      open={open}
      busy={saving}
      onClose={close}
      title="Remove artist"
      description="Their locations are deactivated, not deleted. Items, menus, counts and any settlement history stay exactly as they are."
      footer={
        <>
          <PrimaryButton
            onClick={() => {
              void handleRemove();
            }}
            isDisabled={!canRemove}
            isLoading={saving}
          >
            Remove Artist
          </PrimaryButton>
          <TertiaryButton onClick={close} isDisabled={saving}>
            Cancel
          </TertiaryButton>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        Remove <span className="font-semibold">{shownName}</span> from this
        event's merch configuration?
      </p>

      {!checked && (
        <p className="mt-3 text-xs text-gray-400">
          Checking what's already recorded for this artist…
        </p>
      )}

      {checked && blockers.length === 0 && (
        <>
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-sm text-gray-700">
              {shownCount === 1
                ? "Their 1 location at this event will be deactivated"
                : `Their ${shownCount} locations at this event will be deactivated`}
              , and they'll disappear from this tab and from Merch Manager.
            </p>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Reversible: add a location back for them and they return with the
            split you already agreed — the deal is kept, just switched off.
          </p>
        </>
      )}

      {checked && blockers.length > 0 && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2">
          <p className="text-sm font-semibold text-red-800">
            {shownName} can't be removed from this event.
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {blockers.map((reason) => (
              <li key={reason} className="text-sm text-red-800">
                {reason}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-red-700">
            {usage?.hasSettlement
              ? "An artist who has been counted, sold or settled stays on the event — that's what keeps their history reachable. To stop them selling, remove their individual locations below; to undo a settlement, do it in Merch Manager."
              : "Remove their individual locations below if you need to close one down — that keeps the artist, their counts and their history intact."}
          </p>
        </div>
      )}
    </MerchModal>
  );
};

const RemoveLocationModal = ({
  open,
  location,
  locationLabel,
  artistName,
  isLastLocation,
  onClose,
  onRemoved,
}: {
  open: boolean;
  /** null between closing and unmounting — the dialog animates out. */
  location: SetupLocation | null;
  locationLabel: string;
  artistName: string;
  /** True when this is the only active location the artist has at this event. */
  isLastLocation: boolean;
  onClose: () => void;
  /** Called after a successful deactivate, with whether the artist just lost
   *  their last location — the caller reloads a different amount accordingly. */
  onRemoved: (wasLast: boolean) => Promise<void>;
}) => {
  /** null = unknown (the usage query failed), not zero. */
  const [counts, setCounts] = useState<number | null>(null);
  const [sales, setSales] = useState<number | null>(null);
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);

  const locationId = location?.id ?? null;

  // The dialog animates out for ~200ms AFTER the caller clears its target and
  // reloads the list, so read the copy off the last live values rather than
  // flashing an empty name (or a warning that just became true) on the way out.
  const shown = useRef({ label: locationLabel, isLast: isLastLocation });
  if (location) shown.current = { label: locationLabel, isLast: isLastLocation };
  const shownLabel = location ? locationLabel : shown.current.label;
  const shownIsLast = location ? isLastLocation : shown.current.isLast;

  // network-only: somebody may have counted at this location since the panel
  // opened, and a stale zero here is exactly the number that gets somebody to
  // press the button.
  useEffect(() => {
    if (!open || locationId === null) return;
    let cancelled = false;
    setCountsLoaded(false);
    setCounts(null);
    setSales(null);
    setAcknowledged(false);
    void (async () => {
      try {
        const { data } = await client.query<LocationUsageQueryResult>({
          query: GET_MERCH_LOCATION_USAGE,
          variables: { locationId },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const total = data?.inventory_count_line_aggregate?.aggregate?.count;
        setCounts(typeof total === "number" ? total : null);
        const sold = data?.orders_aggregate?.aggregate?.count;
        setSales(typeof sold === "number" ? sold : null);
      } catch {
        // Unknown, NOT none — the dialog says so rather than claiming zero.
        if (!cancelled) {
          setCounts(null);
          setSales(null);
        }
      } finally {
        if (!cancelled) setCountsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, locationId]);

  const hasCounts = counts !== null && counts > 0;
  const countsUnknown = countsLoaded && counts === null;
  /** Anything with a consequence has to be ticked, not just clicked past. */
  const hasSales = sales !== null && sales > 0;
  const needsAck = hasCounts || hasSales || shownIsLast;
  const canRemove =
    location !== null && countsLoaded && !saving && (!needsAck || acknowledged);

  const close = () => {
    if (saving) return;
    setAcknowledged(false);
    onClose();
  };

  const handleRemove = async () => {
    if (!location || !canRemove) return;
    setSaving(true);
    const wasLast = isLastLocation;
    try {
      const result = await client.mutate<{
        update_locations_by_pk: { id: number } | null;
      }>({
        mutation: DEACTIVATE_MERCH_LOCATION,
        variables: { id: location.id, userId: currentUserId() },
      });
      // by_pk returns null rather than erroring when the row is gone or a
      // permission blocked the write.
      if (!result.data?.update_locations_by_pk) {
        throw new Error(
          "That location wasn't removed — it may have been changed or removed since this list loaded."
        );
      }
      toast.success(
        wasLast
          ? `"${locationLabel}" removed — ${artistName} has no location at this event now`
          : `"${locationLabel}" removed`
      );
      setAcknowledged(false);
      onClose();
      // After close, so a reload can't yank this dialog out mid-write.
      await onRemoved(wasLast);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MerchModal
      open={open}
      busy={saving}
      onClose={close}
      title="Remove location"
      description="The location is deactivated, not deleted — orders, menus and counts that point at it are left exactly as they are."
      footer={
        // [primary, cancel] + MerchModal's flex-row-reverse = Cancel | Remove.
        <>
          <PrimaryButton
            onClick={() => {
              void handleRemove();
            }}
            isDisabled={!canRemove}
            isLoading={saving}
          >
            Remove Location
          </PrimaryButton>
          <TertiaryButton onClick={close} isDisabled={saving}>
            Cancel
          </TertiaryButton>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        Remove <span className="font-semibold">"{shownLabel}"</span> from this
        event?
      </p>

      {!countsLoaded && (
        <p className="mt-3 text-xs text-gray-400">
          Checking what's been recorded at this location…
        </p>
      )}

      {countsLoaded && hasCounts && (
        <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2">
          <p className="text-sm text-yellow-800">
            {counts === 1
              ? "1 counted line was recorded at this location."
              : `${counts} counted lines were recorded at this location.`}{" "}
            They stay in the database but the location will no longer appear here or
            in Merch Manager.
          </p>
        </div>
      )}

      {countsLoaded && hasSales && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-800">
            <span className="font-semibold">
              {sales === 1
                ? "1 order has been rung at this location."
                : `${sales} orders have been rung at this location.`}
            </span>{" "}
            Nothing is deleted — those orders keep pointing at it and every
            report still resolves it. But the location will stop appearing on the
            POS, so don't do this mid-event unless you mean to close it down.
          </p>
        </div>
      )}

      {countsLoaded && countsUnknown && (
        <p className="mt-3 text-xs text-gray-500">
          Couldn't check whether anything has been counted at this location, so
          assume there may be counts. Nothing recorded is deleted either way —
          it just stops being reachable from here and from Merch Manager.
        </p>
      )}

      {countsLoaded && counts === 0 && (
        <p className="mt-3 text-xs text-gray-500">
          Nothing has been counted at this location.
        </p>
      )}

      {/* The loud one: this doesn't just hide a location, it takes the artist off
          the event. Red, not yellow — it is a different order of consequence
          from the counts note above it. */}
      {shownIsLast && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2">
          <p className="text-sm font-semibold text-red-800">
            This is {artistName}'s only location at this event.
          </p>
          <p className="mt-1 text-sm text-red-700">
            Artists are derived from their locations, so removing it REMOVES{" "}
            {artistName} FROM THIS EVENT — they disappear from this tab and from
            Merch Manager's vendor list until a location is added back for them.
            Their deal and fee lines are not deleted and will still be there if
            they return.
          </p>
        </div>
      )}

      {needsAck && (
        <label className="mt-3 flex items-start gap-2">
          {/* No name — see the file header. */}
          <input
            type="checkbox"
            checked={acknowledged}
            disabled={saving || !countsLoaded}
            onChange={(event) => setAcknowledged(event.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">
            {shownIsLast
              ? `I understand ${artistName} will no longer appear on this event.`
              : "I understand what's been counted here will no longer be visible."}
          </span>
        </label>
      )}
    </MerchModal>
  );
};

const LocationMenuEditor = ({
  eventId,
  artist,
  organizationId,
  locked,
  onArtistsChanged,
}: {
  eventId: number;
  artist: MerchArtist;
  organizationId: number | null | undefined;
  locked: boolean;
  /** Reload the whole artist list. Only used when a location was taken off another
   *  vendor — that vendor may have just lost their last location here. */
  onArtistsChanged?: () => void;
}) => {
  const [locations, setLocations] = useState<SetupLocation[]>([]);
  const [menus, setMenus] = useState<SetupMenu[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Attach picker selection, per location id. */
  const [picked, setPicked] = useState<Record<number, number>>({});
  const [addOpen, setAddOpen] = useState(false);
  /** The location the remove dialog is asking about, null when it's closed. */
  const [removing, setRemoving] = useState<SetupLocation | null>(null);
  // Only the option list is wanted here — the hook's own "preferred" default is
  // for the create forms, and must never be pushed onto an existing location. The
  // editor is mounted only while the artist's panel is open, so this is still
  // part of the per-artist lazy load.
  const { locationTypes } = useLocationTypes(true, organizationId);

  // This component is only mounted while the artist's panel is expanded, so
  // mounting IS the lazy load — collapsed artists never hit these tables.
  const load = useCallback(async () => {
    try {
      const { data } = await client.query<VendorSetupQueryResult>({
        query: GET_MERCH_VENDOR_SETUP,
        variables: { eventId, vendorId: artist.id },
        fetchPolicy: "network-only",
      });
      setLocations(data?.locations ?? []);
      setMenus(data?.menus ?? []);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoaded(true);
    }
  }, [eventId, artist.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const menuName = (menu: SetupMenu | null, menuId: number): string =>
    menu?.name?.trim() || `Menu ${menu?.id ?? menuId}`;

  const locationName = (location: SetupLocation): string =>
    location.name?.trim() || `Location ${location.id}`;

  /** Menus of this vendor that aren't already on that location. Attaching the
   *  same menu twice would just make a duplicate join row. */
  const attachable = (location: SetupLocation): SetupMenu[] => {
    const already = new Set(location.location_menus.map((row) => row.menu_id));
    return menus.filter((menu) => !already.has(menu.id));
  };

  /** Menus of this vendor that no location of theirs at this event carries. They
   *  may still be attached to something we can't see from here, so this is
   *  phrased as a heads-up rather than an error. */
  const unattached = useMemo(() => {
    const used = new Set<number>();
    for (const location of locations) {
      for (const row of location.location_menus) used.add(row.menu_id);
    }
    return menus.filter((menu) => !used.has(menu.id));
  }, [locations, menus]);

  const attachMenu = async (location: SetupLocation, menuId: number) => {
    if (locked || busy || !menuId) return;
    setBusy(true);
    try {
      await client.mutate({
        mutation: CREATE_MERCH_LOCATION_MENU,
        variables: { object: { location_id: location.id, menu_id: menuId } },
      });
      toast.success("Menu attached");
      setPicked((current) => ({ ...current, [location.id]: 0 }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
    await load();
    setBusy(false);
  };

  const detachMenu = async (location: SetupLocation, row: SetupLocationMenu) => {
    if (locked || busy) return;
    const label = menuName(row.menu, row.menu_id);
    const confirmed = window.confirm(
      `Detach "${label}" from "${locationName(location)}"?\n\n` +
        "This is how the POS finds that location's items, and it's where Merch " +
        "Manager puts new ones. The menu itself is not deleted and stays on " +
        "any other location it serves."
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const result = await client.mutate<{
        delete_location_menu: { affected_rows: number } | null;
      }>({
        mutation: DELETE_MERCH_LOCATION_MENU,
        variables: { id: row.id },
      });
      // Hasura reports a delete that matched nothing — including one blocked by
      // permissions — as affected_rows: 0 rather than an error.
      if ((result.data?.delete_location_menu?.affected_rows ?? 0) < 1) {
        toast.error(
          "Couldn't detach that menu — it may already be gone. Reloading."
        );
      } else {
        toast.success("Menu detached");
      }
    } catch (err) {
      toast.error(errorMessage(err));
    }
    await load();
    setBusy(false);
  };

  const createMenuFor = async (location: SetupLocation) => {
    if (locked || busy) return;
    setBusy(true);
    let menuId: number | null = null;
    try {
      const created = await client.mutate<{
        insert_menus_one: { id: number } | null;
      }>({
        mutation: CREATE_MERCH_MENU,
        variables: {
          object: merchMenuInput(
            eventId,
            artist.id,
            nextMerchMenuName(artist.name, menus)
          ),
        },
      });
      menuId = created.data?.insert_menus_one?.id ?? null;
      if (menuId === null) throw new Error("The menu wasn't created.");
      await client.mutate({
        mutation: CREATE_MERCH_LOCATION_MENU,
        variables: { object: { location_id: location.id, menu_id: menuId } },
      });
      toast.success("Menu created and attached");
    } catch (err) {
      toast.error(
        menuId === null
          ? errorMessage(err)
          : `Created the menu, but couldn't attach it: ${errorMessage(
              err
            )} It's listed below — attach it from the location.`
      );
    }
    await load();
    setBusy(false);
  };

  const renameLocation = async (id: number, name: string): Promise<boolean> => {
    if (locked) return false;
    try {
      await client.mutate({
        mutation: UPDATE_MERCH_LOCATION,
        variables: { id, input: { name, last_updated_by: currentUserId() } },
      });
      toast.success("Location renamed");
      await load();
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      await load();
      return false;
    }
  };

  /** locations.location_type is a dashboard_reference VALUE, so what's written
   *  is the option's value, never its label. */
  const updateLocationType = async (
    id: number,
    locationType: string
  ): Promise<boolean> => {
    if (locked || locationType === "") return false;
    try {
      await client.mutate({
        mutation: UPDATE_MERCH_LOCATION,
        variables: {
          id,
          input: { location_type: locationType, last_updated_by: currentUserId() },
        },
      });
      toast.success("Location type updated");
      await load();
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      await load();
      return false;
    }
  };

  const renameMenu = async (id: number, name: string): Promise<boolean> => {
    if (locked) return false;
    try {
      await client.mutate({
        mutation: UPDATE_MERCH_MENU,
        variables: { id, input: { name, last_updated_by: currentUserId() } },
      });
      toast.success("Menu renamed");
      await load();
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      await load();
      return false;
    }
  };

  return (
    <div className="mb-4 rounded-md border border-gray-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">Locations &amp; menus</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Where this artist sells, and which menus each location carries. The menu
            attached to a location is where Merch Manager puts new items, and
            what the POS sells from. One menu can serve several locations, and a
            shared location can carry more than one menu. "Add location" can either
            create a new location or hand this artist one that already exists at this
            event. "Remove location" hides one from both apps rather than deleting
            it — anything counted at it is kept.
          </p>
        </div>
        {/* Rule 1: the block's primary action, top-right of the header row. */}
        {!locked && (
          <div className="flex shrink-0 items-center">
            <PrimaryButton onClick={() => setAddOpen(true)} isDisabled={busy}>
              Add location
            </PrimaryButton>
          </div>
        )}
      </div>

      <AddLocationModal
        open={addOpen}
        eventId={eventId}
        artist={artist}
        organizationId={organizationId}
        existingMenus={menus}
        onClose={() => setAddOpen(false)}
        onDone={load}
        onOwnerChanged={onArtistsChanged}
      />

      <RemoveLocationModal
        open={removing !== null}
        location={removing}
        locationLabel={removing ? locationName(removing) : ""}
        artistName={artist.name}
        // The list this is rendered from is already filtered to ACTIVE
        // locations for this vendor at this event, so its length is the count
        // that matters.
        isLastLocation={locations.length <= 1}
        onClose={() => setRemoving(null)}
        onRemoved={async (wasLast) => {
          await load();
          // Losing the last location takes the artist off the tab entirely, and
          // only the section above can re-derive that list.
          if (wasLast) onArtistsChanged?.();
        }}
      />

      {!loaded && (
        <p className="mt-3 text-sm text-gray-400">Loading this artist's setup…</p>
      )}

      {loaded && error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-700">
            Couldn't load the locations and menus. {error}
          </p>
          {/* Rule 2: row-level action, right-aligned on its own row. */}
          <div className="mt-2 flex justify-end">
            <SecondaryButton
              onClick={() => {
                void load();
              }}
            >
              Retry
            </SecondaryButton>
          </div>
        </div>
      )}

      {loaded && !error && locations.length === 0 && (
        <p className="mt-3 text-sm text-yellow-700">
          This artist has no active location at this event. They're only still
          listed here because of a deal or settlement on file — add a location
          so they can sell.
        </p>
      )}

      {/* One CARD per location, with its menus nested inside it — tinted,
          left-ruled and one size down, so the location and the thing it sells from
          can never be mistaken for two lines of the same list. Cards are
          separated by real space (space-y-3), not a hairline. */}
      {loaded && !error && locations.length > 0 && (
        <div className="mt-4 space-y-3">
          {locations.map((location) => {
            const rows = location.location_menus ?? [];
            const options = attachable(location);
            return (
              <div
                key={location.id}
                className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
              >
                {/* Location header row — marker + name left, primary action right. */}
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <TypeTag kind="location" />
                      {/* The type sits where it always did; the pencil next to
                          it is the same gesture as the rename below. When the
                          reference list didn't load there's nothing to pick
                          from, so it stays plain text. */}
                      <InlineSelect
                        value={location.location_type ?? ""}
                        options={locationTypes}
                        emptyLabel="No type"
                        title="Change location type"
                        disabled={locked || busy}
                        onSave={(value) => updateLocationType(location.id, value)}
                      />
                    </div>
                    <div className="mt-1 min-w-0 text-sm font-semibold text-gray-900">
                      <InlineRename
                        value={locationName(location)}
                        title="Rename location"
                        disabled={locked || busy}
                        onSave={(name) => renameLocation(location.id, name)}
                      />
                    </div>
                  </div>
                  {!locked && (
                    // Rule 1: the card's primary action, top-right. Remove sits
                    // beside it as red TEXT — destructive, and deliberately not
                    // competing with the filled button next to it. PrimaryButton
                    // carries its own ml-2, so no gap class here.
                    <div className="flex shrink-0 items-center">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          // Same belt-and-braces as the rename paths: the
                          // button is already hidden when locked.
                          if (locked || busy) return;
                          setRemoving(location);
                        }}
                        className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-40"
                      >
                        <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        Remove location
                      </button>
                      <PrimaryButton
                        onClick={() => {
                          void createMenuFor(location);
                        }}
                        isDisabled={busy}
                      >
                        Create menu
                      </PrimaryButton>
                    </div>
                  )}
                </div>

                {/* Everything below is SUBORDINATE to the location above it. */}
                <div className="mt-3 rounded-md border-l-4 border-gray-300 bg-gray-50 py-2 pl-3 pr-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Menus on this location
                  </p>

                  {rows.length === 0 ? (
                    <p className="mt-1 text-xs font-medium text-yellow-700">
                      No menu attached — items can't be added here yet.
                    </p>
                  ) : (
                    <ul className="mt-1 divide-y divide-gray-200">
                      {rows.map((row) => (
                        <li
                          key={row.id}
                          className="flex items-center justify-between gap-2 py-1.5"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <TypeTag kind="menu" />
                            <span className="min-w-0 text-xs text-gray-700">
                              <InlineRename
                                value={menuName(row.menu, row.menu_id)}
                                title="Rename menu"
                                disabled={locked || busy}
                                onSave={(name) =>
                                  renameMenu(row.menu?.id ?? row.menu_id, name)
                                }
                              />
                            </span>
                          </span>
                          {/* Destructive: red TEXT, not a red button — this is
                              not the thing anyone should be reaching for. */}
                          {!locked && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                void detachMenu(location, row);
                              }}
                              className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-40"
                            >
                              <XMarkIcon
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                              />
                              Detach
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* The attach control lives INSIDE the location it attaches to. */}
                  {!locked &&
                    (options.length > 0 ? (
                      // gap-Y only: PrimaryButton already carries ml-2, and a
                      // horizontal gap on top of it double-spaces the row.
                      <div className="mt-2 flex flex-wrap items-end justify-end gap-y-2 border-t border-gray-200 pt-2">
                        <div className="w-full sm:w-64 sm:flex-1">
                          {/* No `name` on CustomSelect — see the file header.
                              No `error`/`required` either: this is an ordinary
                              picker, and it must not render red unless
                              something is genuinely wrong. */}
                          <CustomSelect
                            title="Attach an existing menu"
                            isDisabled={busy}
                            value={picked[location.id] ?? 0}
                            onChange={(value) =>
                              setPicked((current) => ({
                                ...current,
                                [location.id]: Number(value ?? 0),
                              }))
                            }
                            options={[
                              { value: 0, label: "Select a menu…" },
                              ...options.map((menu) => ({
                                value: menu.id,
                                label: menuName(menu, menu.id),
                              })),
                            ]}
                          />
                        </div>
                        <div className="flex items-center pb-0.5">
                          <PrimaryButton
                            onClick={() => {
                              void attachMenu(
                                location,
                                picked[location.id] ?? 0
                              );
                            }}
                            isDisabled={busy || !(picked[location.id] ?? 0)}
                          >
                            Attach
                          </PrimaryButton>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-gray-400">
                        {menus.length === 0
                          ? "This artist has no menus at this event yet."
                          : "Every menu this artist has is already on this location."}
                      </p>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loaded && !error && unattached.length > 0 && (
        // No gray tint on this one: bg-gray-50 now MEANS "menus of the location
        // above", and a tinted block floating under the cards reads as one.
        <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
          Not on any of this artist's locations at this event:{" "}
          {unattached.map((menu) => menuName(menu, menu.id)).join(", ")}. Nothing
          sells from a menu that isn't attached — attach it above, or leave it if
          it's serving a location somewhere else.
        </p>
      )}

      {locked && (
        <p className="mt-3 text-xs text-gray-400">
          The settlement is locked, so this artist's setup can't be changed here.
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// split editor
// ---------------------------------------------------------------------------

const SplitEditor = ({
  eventId,
  artist,
  deal,
  managers,
  managersAreEventScoped,
  resolveVendorName,
  onAddManager,
  onInvite,
  isInvited,
  locked,
  onSaved,
}: {
  eventId: number;
  artist: MerchArtist;
  deal: MerchVendorDeal | undefined;
  /** Managers assigned to THIS event (or the org-wide fallback list). */
  managers: ManagerCandidate[];
  managersAreEventScoped: boolean;
  resolveVendorName: (vendorId: number) => string;
  onAddManager: () => void;
  /** Opens the shared invite modal — see MerchVendorDeals. */
  onInvite: (target: InviteTarget) => void;
  /** Already invited for this vendor+role during this page visit. */
  isInvited: (role: MerchInviteRole, vendorId: number) => boolean;
  locked: boolean;
  /** Reload the deals — used to resync after a failed autosave. */
  onSaved: () => Promise<void>;
}) => {
  const [mode, setMode] = useState<SplitMode>(
    deal?.split_mode === "nested" ? "nested" : "flat"
  );
  const [artistPct, setArtistPct] = useState(
    deal ? String(num(deal.artist_pct)) : ""
  );
  const [organizerPct, setOrganizerPct] = useState(
    deal ? String(num(deal.organizer_pct)) : ""
  );
  const [managerPct, setManagerPct] = useState(
    deal ? String(num(deal.manager_pct)) : "0"
  );
  const [managerVendorId, setManagerVendorId] = useState<number>(
    deal?.manager_vendor_id ?? 0
  );

  // What this artist's split looked like when the panel opened. The hidden
  // input below is only emitted when the CURRENT values differ from this.
  //
  // That test is load-bearing, not an optimisation: an artist with NO
  // merch_vendor_deal row is on the legacy two-party model, and the festival's
  // cut comes from vendors.revenue_share. Emitting a payload for every artist
  // on every event save would mint placeholder rows for artists nobody touched
  // and silently move them onto the three-party model.
  const initial = useRef({
    mode: deal?.split_mode === "nested" ? "nested" : "flat",
    artistPct: deal ? String(num(deal.artist_pct)) : "",
    organizerPct: deal ? String(num(deal.organizer_pct)) : "",
    managerPct: deal ? String(num(deal.manager_pct)) : "0",
    managerVendorId: deal?.manager_vendor_id ?? 0,
  });

  const artistN = Number(artistPct);
  const organizerN = Number(organizerPct);
  const managerN = Number(managerPct);
  const hasManager = managerVendorId !== 0;

  const inRange = (n: number) => Number.isFinite(n) && n >= 0 && n <= 100;
  const safe = (n: number) => (Number.isFinite(n) ? n : 0);

  // Round EACH share first: numeric(5,2) rounds per column on write, so
  // 33.333 x 3 stores as 99.99, not 100. Rounding the sum instead would let a
  // deal through that the CHECK constraint then rejects.
  const flatTotal = round2(
    round2(safe(artistN)) + round2(safe(organizerN)) + round2(safe(managerN))
  );
  const flatOk =
    artistPct !== "" &&
    organizerPct !== "" &&
    managerPct !== "" &&
    inRange(artistN) &&
    inRange(organizerN) &&
    inRange(managerN) &&
    flatTotal === 100;
  const nestedOk =
    organizerPct !== "" &&
    managerPct !== "" &&
    inRange(organizerN) &&
    inRange(managerN);
  const nested = nestedShares(safe(organizerN), safe(managerN));
  const managerNeedsVendor = safe(managerN) > 0 && !hasManager;
  const valid = (mode === "flat" ? flatOk : nestedOk) && !managerNeedsVendor;

  /**
   * The picker only lists managers assigned to this event, but a deal saved
   * earlier can point at a manager who never was (or has since been removed).
   * react-select renders a value with no matching option as EMPTY, which reads
   * as "None" — and the next Save Split would then write null and silently drop
   * a manager the contract says gets paid. Pin the current selection into the
   * list, flagged, so it survives a round trip and the mismatch is visible.
   */
  const managerOptions = useMemo(() => {
    const options: { value: number; label: string }[] = [
      { value: 0, label: "None" },
      ...managers.map((manager) => ({
        value: manager.id,
        label: manager.name,
      })),
    ];
    if (
      managerVendorId !== 0 &&
      !managers.some((manager) => manager.id === managerVendorId)
    ) {
      options.push({
        value: managerVendorId,
        label: `${resolveVendorName(managerVendorId)} (not assigned to this event)`,
      });
    }
    return options;
  }, [managers, managerVendorId, resolveVendorName]);

  const selectedIsUnassigned =
    managerVendorId !== 0 &&
    !managers.some((manager) => manager.id === managerVendorId);

  const pickManager = (id: number) => {
    setManagerVendorId(id);
    // No third party on the deal: their cut goes with them.
    if (id === 0) setManagerPct("0");
  };

  const selfManaged = hasManager && managerVendorId === artist.id;

  const dirty =
    mode !== initial.current.mode ||
    artistPct !== initial.current.artistPct ||
    organizerPct !== initial.current.organizerPct ||
    managerPct !== initial.current.managerPct ||
    managerVendorId !== initial.current.managerVendorId;

  /**
   * The row to upsert, or null when there's nothing to write.
   *
   * Null when: locked (frozen), unchanged (see `initial` — writing a row for an
   * untouched artist would move them off the legacy two-party model), invalid,
   * or the artist is their own manager.
   */
  const payload = (() => {
    if (locked || !dirty || !valid || selfManaged) return null;
    const organizerValue = round2(safe(organizerN));
    const managerValue = hasManager ? round2(safe(managerN)) : 0;
    // artist_pct is DERIVED in nested mode — store the exact number the
    // settlement math will use, never the rounded preview shown above.
    let artistValue = round2(safe(artistN));
    if (mode === "nested") {
      const afterOrganizer = 1 - organizerValue / 100;
      artistValue = round2(afterOrganizer * 100 - afterOrganizer * managerValue);
    }
    return {
      event_id: eventId,
      vendor_id: artist.id,
      split_mode: mode,
      artist_pct: artistValue,
      organizer_pct: organizerValue,
      manager_pct: managerValue,
      manager_vendor_id: hasManager ? managerVendorId : null,
      // Intent flag only. The ACTUAL decision to pay a manager in-app is made
      // at payout time by choosing a Stripe destination for them in Inventory
      // Manager's settlement — that's what ronin-api pays off. This column
      // stays false here so the config screen never implies a payout that the
      // payout screen hasn't been pointed at.
      manager_paid_in_app: false,
      is_active: true,
    };
  })();

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  /**
   * Autosave, DEBOUNCED — these are typed percentage fields, and a write per
   * keystroke would both hammer the API and fire the flat-total-100 CHECK
   * constraint on every intermediate value ("5" on the way to "50").
   *
   * Keyed on the serialised payload so it re-arms only when the values that
   * would actually be written change. `payload` is null while the split is
   * incomplete or invalid, so a half-typed split simply never fires.
   *
   * On success the baseline moves to what was just written — otherwise the row
   * stays "dirty" forever and every later edit re-sends it.
   */
  const serialised = payload ? JSON.stringify(payload) : null;
  useEffect(() => {
    if (serialised === null) return;
    const timer = setTimeout(() => {
      void (async () => {
        setSaving(true);
        try {
          await client.mutate({
            mutation: UPSERT_MERCH_VENDOR_DEAL,
            variables: {
              object: {
                ...JSON.parse(serialised),
                updated_at: new Date().toISOString(),
                last_updated_by: currentUserId(),
              },
            },
          });
          initial.current = {
            mode,
            artistPct,
            organizerPct,
            managerPct,
            managerVendorId,
          };
          setSavedAt(Date.now());
        } catch (error) {
          toast.error(errorMessage(error));
          // A failure can mean it locked under us — reload so the panel shows
          // the real state rather than the values the user just typed.
          await onSaved();
        } finally {
          setSaving(false);
        }
      })();
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialised]);

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">Revenue split</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Who gets what before any fee line is applied. This freezes with the
            settlement. Changes save on their own.
          </p>
        </div>
        {/* Mode switch only — the split saves itself, like the fee lines below
            it, so there is no per-row Save button. */}
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-gray-400">
            {saving ? "Saving…" : savedAt !== null ? "Saved" : null}
          </span>
          <Segmented
            value={mode}
            disabled={locked}
            onChange={(value) => setMode(value === "nested" ? "nested" : "flat")}
            options={[
              { value: "flat", label: "Flat" },
              { value: "nested", label: "Nested" },
            ]}
          />
        </div>
      </div>

      {/* Only surfaced when the split is dirty AND won't save — a valid one
          saves itself a moment later and needs no commentary. */}
      {dirty && !locked && !payload && (
        <p className="mt-2 text-xs text-gray-500">
          {selfManaged
            ? "The artist can't also be the vendor manager — this isn't saved."
            : "Not saved yet: finish the split and it saves on its own."}
        </p>
      )}

      {mode === "flat" ? (
        <>
          <div className="mt-4 grid grid-cols-6 gap-4">
            <div className="col-span-6 sm:col-span-2">
              <PercentField
                label="Artist %"
                value={artistPct}
                onChange={setArtistPct}
                disabled={locked}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <PercentField
                label="Organizer %"
                value={organizerPct}
                onChange={setOrganizerPct}
                disabled={locked}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <PercentField
                label="Manager %"
                value={managerPct}
                onChange={setManagerPct}
                disabled={locked || !hasManager}
                hint={hasManager ? undefined : "Select a vendor manager first"}
              />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total
            </span>
            <span
              className={`text-sm font-semibold ${
                flatTotal === 100 ? "text-green-600" : "text-red-600"
              }`}
            >
              {fmtPct(flatTotal)}%
            </span>
          </div>
          {flatTotal !== 100 && (
            <p className="mt-1 text-xs text-gray-500">
              Flat percentages are shares of adjusted gross and have to add up to
              exactly 100%.
            </p>
          )}
        </>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-6 gap-4">
            <div className="col-span-6 sm:col-span-3">
              <PercentField
                label="Organizer % (of net)"
                value={organizerPct}
                onChange={setOrganizerPct}
                disabled={locked}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <PercentField
                label="Manager % (of what's left)"
                value={managerPct}
                onChange={setManagerPct}
                disabled={locked || !hasManager}
                hint={hasManager ? undefined : "Select a vendor manager first"}
              />
            </div>
          </div>
          <p className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
            {nestedOk
              ? `Organizer ${fmtPct(
                  organizerN
                )}% → manager ${fmtPct(managerN)}% of the remaining ${fmtPct(
                  nested.remaining
                )}% → artist keeps ${fmtPct(nested.artistEffective)}%`
              : "Enter both percentages to see what the artist keeps."}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            The manager's share is taken from the remainder, so these two can add
            up to more than 100% and still be a valid deal. The artist's share is
            calculated from them and saved automatically.
          </p>
        </>
      )}

      <div className="mt-4 grid grid-cols-6 gap-4">
        <div className="col-span-6 sm:col-span-3">
          <CustomSelect
            title="Vendor manager"
            isDisabled={locked}
            value={managerVendorId}
            onChange={(value) => pickManager(Number(value ?? 0))}
            options={managerOptions}
          />
          {managersAreEventScoped && (
            <p className="mt-1 text-xs text-gray-400">
              Only vendor managers assigned to this event are listed.
            </p>
          )}
        </div>
      </div>
      {managers.length === 0 && !locked && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 text-xs text-gray-500">
            No vendor managers are assigned to this event yet.
          </p>
          <div className="flex shrink-0 items-center">
            <PrimaryButton onClick={onAddManager}>
              Add vendor manager
            </PrimaryButton>
          </div>
        </div>
      )}
      {/* Rule 2: a row-level action, right-aligned on its own row. Not gated on
          `locked` — giving the manager a login has nothing to do with whether
          the numbers are frozen. */}
      {hasManager && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 text-xs text-gray-500">
            {resolveVendorName(managerVendorId)} can be given a Merch Manager
            login for this event.
          </p>
          <div className="flex shrink-0 items-center">
            <PrimaryButton
              onClick={() =>
                onInvite({
                  vendorId: managerVendorId,
                  role: "vendor_manager",
                  vendorName: resolveVendorName(managerVendorId),
                })
              }
            >
              {isInvited("vendor_manager", managerVendorId)
                ? "Resend invite"
                : "Invite"}
            </PrimaryButton>
          </div>
        </div>
      )}
      {selectedIsUnassigned && (
        <p className="mt-2 text-xs text-yellow-700">
          The saved vendor manager isn't assigned to this event. It's kept
          selected so saving won't drop it — add them to the event, or pick
          someone else.
        </p>
      )}
      {hasManager && (
        <p className="mt-2 text-xs text-gray-500">
          The manager's cut always appears on the signed statement. Whether it's
          PAID through the app is decided at payout time: in Merch Manager's
          settlement, choosing a Stripe destination for the manager pays them as
          their own leg — leaving it empty means they settle outside the app.
        </p>
      )}
      {managerNeedsVendor && (
        <p className="mt-2 text-xs text-red-600">
          A manager cut needs a vendor manager selected.
        </p>
      )}
      {/* Save lives in the header row above — see the layout rules in the file
          header. It is deliberately NOT repeated down here. */}
    </div>
  );
};

// ---------------------------------------------------------------------------
// fee / deduction editor
// ---------------------------------------------------------------------------

const FeeEditor = ({
  eventId,
  artist,
  settlement,
  locked,
  onSaved,
}: {
  eventId: number;
  artist: MerchArtist;
  settlement: VendorSettlement | undefined;
  locked: boolean;
  onSaved: () => Promise<void>;
}) => {
  const [type, setType] = useState("credit_card_fee");
  const [label, setLabel] = useState("");
  const [flatOrPct, setFlatOrPct] = useState("pct");
  const [rate, setRate] = useState("");
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [offTop, setOffTop] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Remount key for the label Input, which keeps its own internal state.
  const [formKey, setFormKey] = useState(0);

  const lines = settlement?.lines ?? [];
  const rateN = Number(rate);
  const amountN = Number(amount ?? NaN);
  const valid =
    flatOrPct === "pct"
      ? Number.isFinite(rateN) && rateN > 0 && rateN <= 100
      : Number.isFinite(amountN) && amountN > 0;

  const resetForm = () => {
    setLabel("");
    setRate("");
    setAmount(undefined);
    setFormKey((key) => key + 1);
  };

  const handleAdd = async () => {
    if (locked || !valid) return;
    setAdding(true);
    try {
      // The settlement row may not exist yet — nothing creates one up front.
      const ensured = await client.mutate<{
        insert_vendor_settlement_one: {
          id: number;
          status: string | null;
          locked_at: string | null;
        } | null;
      }>({
        mutation: ENSURE_VENDOR_SETTLEMENT,
        variables: {
          object: {
            event_id: eventId,
            vendor_id: artist.id,
            last_updated_by: currentUserId(),
          },
        },
      });
      const row = ensured.data?.insert_vendor_settlement_one;
      if (!row) throw new Error("Couldn't open a settlement for this artist.");
      if (row.locked_at) {
        toast.error(
          "This settlement locked — fees can no longer be added for this artist."
        );
        await onSaved();
        return;
      }
      await client.mutate({
        mutation: INSERT_VENDOR_SETTLEMENT_LINE,
        variables: {
          object: {
            settlement_id: row.id,
            type,
            label: label.trim() === "" ? type.replace(/_/g, " ") : label.trim(),
            flat_or_pct: flatOrPct,
            rate: flatOrPct === "pct" ? round2(rateN) : null,
            amount_cents:
              flatOrPct === "flat" ? Math.round(amountN * 100) : 0,
            off_top: offTop,
            kept_by: "festival",
            last_updated_by: currentUserId(),
          },
        },
      });
      toast.success("Fee added");
      resetForm();
      await onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
      await onSaved();
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (lineId: number) => {
    if (locked) return;
    setDeletingId(lineId);
    try {
      const result = await client.mutate<{
        delete_vendor_settlement_line: { affected_rows: number } | null;
      }>({
        mutation: DELETE_VENDOR_SETTLEMENT_LINE,
        variables: { id: lineId },
      });
      // 0 rows = the line is already gone OR its settlement locked meanwhile.
      // The mutation succeeds either way, so this is the only signal we get.
      if ((result.data?.delete_vendor_settlement_line?.affected_rows ?? 0) < 1) {
        toast.error(
          "Couldn't delete that fee — the settlement may be locked. Reloading."
        );
      } else {
        toast.success("Fee removed");
      }
      await onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
      await onSaved();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-gray-200 p-4">
      <p className="text-sm font-semibold text-gray-800">Fees & deductions</p>
      <p className="mt-0.5 text-xs text-gray-500">
        Assigned per artist from their contract, before the event — nothing is
        added at settlement time. Percentage fees compute off net (credit card
        fees off credit receipts); off-top lines come out before the split.
      </p>

      <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
        {lines.length === 0 && (
          <p className="py-3 text-sm text-gray-400">
            No fees configured for this artist yet.
          </p>
        )}
        {lines.map((line) => (
          <div
            key={line.id}
            className="flex items-center justify-between gap-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-800">
                {line.label || line.type.replace(/_/g, " ")}
              </p>
              <p className="text-xs text-gray-500">
                {line.type.replace(/_/g, " ")} · {lineValue(line)} ·{" "}
                {line.off_top ? "off top" : "after split"}
                {line.kept_by ? ` · kept by ${line.kept_by}` : ""}
              </p>
            </div>
            {!locked && (
              <button
                type="button"
                title="Remove fee"
                disabled={deletingId === line.id}
                onClick={() => {
                  void handleDelete(line.id);
                }}
                className="shrink-0 p-1 text-gray-400 hover:text-red-600 disabled:opacity-40"
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">Remove fee</span>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* The fee form is its own sub-block, so the rule still holds: its
          primary action sits top-right of ITS header row, next to the fields it
          submits rather than stranded at the bottom of the card. */}
      {!locked && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-800">New fee line</p>
            <div className="flex shrink-0 items-center">
              <PrimaryButton
                onClick={() => {
                  void handleAdd();
                }}
                isDisabled={!valid || adding}
                isLoading={adding}
              >
                Add fee
              </PrimaryButton>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-6 gap-4">
            <div className="col-span-6 sm:col-span-2">
              <CustomSelect
                title="Type"
                value={type}
                onChange={(value) => setType(String(value ?? "custom"))}
                options={FEE_TYPES}
              />
            </div>
            <div className="col-span-6 sm:col-span-2" key={`label-${formKey}`}>
              <Input
                label="Label"
                name=""
                value={label}
                placeholder={type.replace(/_/g, " ")}
                onChange={(value) => setLabel(String(value ?? ""))}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              {flatOrPct === "pct" ? (
                <PercentField label="Rate" value={rate} onChange={setRate} />
              ) : (
                <PriceInput
                  label="Amount"
                  name=""
                  value={amount ?? ""}
                  onChange={(value) => setAmount(value)}
                />
              )}
            </div>
            <div className="col-span-6 sm:col-span-3">
              <p className="mb-1 block text-sm font-medium text-gray-700">
                Charged as
              </p>
              <Segmented
                value={flatOrPct}
                onChange={setFlatOrPct}
                options={[
                  { value: "pct", label: "Percentage" },
                  { value: "flat", label: "Flat $" },
                ]}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <p className="mb-1 block text-sm font-medium text-gray-700">
                Taken
              </p>
              <Segmented
                value={offTop ? "off_top" : "after_split"}
                onChange={(value) => setOffTop(value === "off_top")}
                options={[
                  { value: "off_top", label: "Off the top" },
                  { value: "after_split", label: "After split" },
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// section
// ---------------------------------------------------------------------------

export const MerchVendorDeals = ({
  eventId,
  organizationId,
}: {
  eventId: number;
  organizationId: number | null | undefined;
}) => {
  const [artists, setArtists] = useState<MerchArtist[]>([]);
  const [deals, setDeals] = useState<MerchVendorDeal[]>([]);
  const [settlements, setSettlements] = useState<VendorSettlement[]>([]);
  const [managers, setManagers] = useState<ManagerCandidate[]>([]);
  // False once we've had to fall back to the org-wide candidate list (see
  // loadManagers) — the picker is then NOT filtered to this event and the UI
  // shouldn't claim otherwise.
  const [managersAreEventScoped, setManagersAreEventScoped] = useState(false);
  const [orgVendors, setOrgVendors] = useState<ManagerCandidate[]>([]);
  const [addArtistOpen, setAddArtistOpen] = useState(false);
  const [addManagerOpen, setAddManagerOpen] = useState(false);
  // Invites are fire-and-forget (merch_invite isn't read back — see the
  // merchInvite section at the top of this file), so "have we sent one?" lives
  // HERE, at the top of the section, and only for this page visit. Kept above
  // the rows so collapsing an artist panel doesn't forget it. A reload does.
  const [inviteTarget, setInviteTarget] = useState<InviteTarget | null>(null);
  const [invitedKeys, setInvitedKeys] = useState<Record<string, boolean>>({});
  const isInvited = useCallback(
    (role: MerchInviteRole, vendorId: number) =>
      Boolean(invitedKeys[inviteKey(role, vendorId)]),
    [invitedKeys]
  );
  const [loading, setLoading] = useState(true);
  const [artistsError, setArtistsError] = useState<string | null>(null);
  const [dealsError, setDealsError] = useState<string | null>(null);
  const [openVendorId, setOpenVendorId] = useState<number | null>(null);
  /** Artist queued for removal from this event. Held here rather than in the
   *  panel so the dialog survives the panel collapsing behind it. */
  const [removeArtist, setRemoveArtist] = useState<MerchArtist | null>(null);
  // The Tab component mounts EVERY panel and hides the inactive ones with
  // `display: none`, so this component is alive on every event edit page load.
  // Don't touch the merch tables until the tab is actually on screen — a hidden
  // element never intersects, so this fires exactly once, when it's revealed.
  const rootRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  /** Deals + settlements only — used after every write. Kept separate from the
   *  artist list so a merch-table permission problem can't blank the list. */
  const loadDeals = useCallback(async () => {
    try {
      const { data } = await client.query<DealsQueryResult>({
        query: GET_EVENT_MERCH_DEALS,
        variables: { eventId },
        fetchPolicy: "network-only",
      });
      setDeals(data?.merch_vendor_deal ?? []);
      setSettlements(data?.vendor_settlement ?? []);
      setDealsError(null);
    } catch (error) {
      setDealsError(errorMessage(error));
    }
  }, [eventId]);

  /** Active vendors in the event's org. Feeds "Add existing vendor" and doubles
   *  as the manager fallback list. Returns the list so callers don't have to
   *  wait a render for state. */
  const loadOrgVendors = useCallback(async (): Promise<ManagerCandidate[]> => {
    if (!organizationId) {
      setOrgVendors([]);
      return [];
    }
    try {
      const { data } = await client.query<ManagersQueryResult>({
        query: GET_MERCH_DEAL_MANAGER_CANDIDATES,
        variables: { organizationId },
        fetchPolicy: "network-only",
      });
      const list = (data?.vendors ?? []).map((vendor) => ({
        id: vendor.id,
        name: vendor.name ?? `Vendor ${vendor.id}`,
      }));
      setOrgVendors(list);
      return list;
    } catch {
      setOrgVendors([]);
      return [];
    }
  }, [organizationId]);

  /**
   * The manager picker's list. Scoped to this event via merch_event_manager.
   *
   * That table arrived in migration 008. If it hasn't been applied — or Hasura
   * hasn't tracked it — the query fails at validation, not with an empty list,
   * and a hard failure here would leave ops with a dead picker on an otherwise
   * working page. So: fall back to the org-wide candidate list, which is the
   * exact behaviour this section had before 008, and flag it so the UI stops
   * promising the list is filtered.
   */
  const loadManagers = useCallback(async () => {
    try {
      const { data } = await client.query<EventManagersQueryResult>({
        query: GET_EVENT_MERCH_MANAGERS,
        variables: { eventId },
        fetchPolicy: "network-only",
      });
      const list = (data?.merch_event_manager ?? [])
        .map((row) => ({
          id: row.vendor?.id ?? row.vendor_id,
          name: row.vendor?.name ?? `Vendor ${row.vendor_id}`,
        }))
        .filter((manager) => Number.isFinite(manager.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      setManagers(list);
      setManagersAreEventScoped(true);
      return;
    } catch {
      setManagersAreEventScoped(false);
    }
    setManagers(await loadOrgVendors());
  }, [eventId, loadOrgVendors]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.query<ArtistsQueryResult>({
        query: GET_EVENT_MERCH_ARTISTS,
        variables: { eventId },
        fetchPolicy: "network-only",
      });
      // A vendor can own several locations at one event — de-dupe by vendor id.
      const byId = new Map<number, MerchArtist>();
      for (const location of data?.locations ?? []) {
        const vendor = location.vendor;
        if (!vendor) continue;
        if (byId.has(vendor.id)) continue;
        byId.set(vendor.id, {
          id: vendor.id,
          name: vendor.name ?? `Vendor ${vendor.id}`,
          type: vendor.type ?? null,
        });
      }
      setArtists(
        Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
      );
      setArtistsError(null);
    } catch (error) {
      setArtistsError(errorMessage(error));
    }

    await loadDeals();
    await loadOrgVendors();
    // Non-fatal by construction: the worst case leaves the dropdown at "None".
    await loadManagers();
    setLoading(false);
  }, [eventId, loadDeals, loadOrgVendors, loadManagers]);

  useEffect(() => {
    if (visible) return;
    const node = rootRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    void loadAll();
  }, [visible, loadAll]);

  const dealsByVendor = useMemo(() => {
    const map = new Map<number, MerchVendorDeal>();
    for (const deal of deals) {
      if (deal.is_active === false) continue;
      map.set(deal.vendor_id, deal);
    }
    return map;
  }, [deals]);

  const settlementsByVendor = useMemo(() => {
    const map = new Map<number, VendorSettlement>();
    for (const settlement of settlements) map.set(settlement.vendor_id, settlement);
    return map;
  }, [settlements]);

  /** Names for every vendor we've seen, so a manager who isn't in the picker's
   *  list can still be labelled rather than shown as a bare id. */
  const vendorNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const vendor of orgVendors) map.set(vendor.id, vendor.name);
    for (const manager of managers) map.set(manager.id, manager.name);
    for (const artist of artists) map.set(artist.id, artist.name);
    return map;
  }, [orgVendors, managers, artists]);

  const resolveVendorName = useCallback(
    (vendorId: number) => vendorNames.get(vendorId) ?? `Vendor ${vendorId}`,
    [vendorNames]
  );

  /** Org vendors with no location at this event — the "Add existing vendor"
   *  list. Adding one already here would only make a duplicate location. */
  const availableVendors = useMemo(() => {
    const present = new Set(artists.map((artist) => artist.id));
    return orgVendors.filter((vendor) => !present.has(vendor.id));
  }, [orgVendors, artists]);

  const canAdd = Boolean(organizationId);

  return (
    <div ref={rootRef} className="col-span-6 border-t border-gray-200 pt-5">
      {/* Event-level and self-saving, so it sits ABOVE the per-artist rows —
          it applies to every artist here, and it's the thing that has to be set
          before anyone can add an item at all.

          Gated on `visible` for the same reason everything else here is: the
          Tab component mounts every panel and hides the inactive ones with
          display:none, so an ungated child would query on every event edit
          page load whether or not anyone opens this tab. */}
      {visible && (
        <ItemCategoryPicker eventId={eventId} organizationId={organizationId} />
      )}

      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm font-semibold text-gray-800">
          Per-Artist Deals &amp; Fees
        </p>
        {/* Rule 1: the section's actions, top-right of the section header. No
            `gap` — PrimaryButton brings its own ml-2. */}
        <div className="flex shrink-0 items-center">
          <PrimaryButton
            onClick={() => setAddManagerOpen(true)}
            isDisabled={!canAdd}
          >
            Add vendor manager
          </PrimaryButton>
          <PrimaryButton
            onClick={() => setAddArtistOpen(true)}
            isDisabled={!canAdd && availableVendors.length === 0}
          >
            Add artist
          </PrimaryButton>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        The revenue split and fee lines Merch Manager settles each artist with.
        These save on their own — they are not part of the event form, so the
        Save button below does not apply to them. An artist appears here once
        they have an active location at this event. No split configured means the
        legacy two-party model (the vendor's own revenue share) still applies.
        Expanding an artist also shows their locations and the menus attached to
        them.
      </p>

      <AddArtistModal
        open={addArtistOpen}
        eventId={eventId}
        organizationId={organizationId}
        availableVendors={availableVendors}
        onClose={() => setAddArtistOpen(false)}
        onDone={loadAll}
      />
      <AddManagerModal
        open={addManagerOpen}
        eventId={eventId}
        organizationId={organizationId}
        onClose={() => setAddManagerOpen(false)}
        onDone={loadManagers}
      />
      {/* One modal for every invite button in this section, re-targeted rather
          than mounted per row. */}
      <InviteModal
        open={inviteTarget !== null}
        eventId={eventId}
        target={inviteTarget}
        alreadySent={
          inviteTarget !== null &&
          isInvited(inviteTarget.role, inviteTarget.vendorId)
        }
        onClose={() => setInviteTarget(null)}
        onSent={(target) =>
          setInvitedKeys((previous) => ({
            ...previous,
            [inviteKey(target.role, target.vendorId)]: true,
          }))
        }
      />

      {/* Same pattern: one dialog, re-targeted per row. */}
      <RemoveArtistModal
        open={removeArtist !== null}
        eventId={eventId}
        artist={removeArtist}
        onClose={() => setRemoveArtist(null)}
        onRemoved={async () => {
          // The artist list is derived from locations, so it has to be
          // re-derived, not patched — and the deals reload with it because the
          // deal row is now inactive.
          setOpenVendorId(null);
          await loadAll();
        }}
      />

      {loading && (
        <p className="mt-4 text-sm text-gray-400">Loading artists…</p>
      )}

      {!loading && artistsError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-sm text-red-700">
            Couldn't load this event's artists. {artistsError}
          </p>
          <div className="mt-2 flex justify-end">
            <SecondaryButton
              onClick={() => {
                void loadAll();
              }}
            >
              Retry
            </SecondaryButton>
          </div>
        </div>
      )}

      {!loading && !artistsError && dealsError && (
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2">
          <p className="text-sm text-yellow-800">
            Couldn't load the saved merch deals. {dealsError}
          </p>
        </div>
      )}

      {!loading && !artistsError && artists.length === 0 && (
        <p className="mt-4 text-sm text-gray-400">
          No active locations at this event yet, so there are no artists to
          configure. Use "Add artist" above to create one and place them here.
        </p>
      )}

      {!loading && !artistsError && artists.length > 0 && (
        <div className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200">
          {artists.map((artist) => {
            const deal = dealsByVendor.get(artist.id);
            const settlement = settlementsByVendor.get(artist.id);
            const locked = Boolean(settlement?.locked_at);
            const feeCount = settlement?.lines?.length ?? 0;
            const open = openVendorId === artist.id;
            return (
              <div key={artist.id}>
                <button
                  type="button"
                  onClick={() => setOpenVendorId(open ? null : artist.id)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 ${
                    open ? "bg-gray-50" : ""
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-800">
                      {artist.name}
                      {locked && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                          Locked
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-gray-500">
                      Split {splitSummary(deal)}
                      {deal ? ` (${deal.split_mode})` : ""} · {feeCount} fee
                      {feeCount === 1 ? "" : "s"}
                    </span>
                  </span>
                  <svg
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${
                      open ? "rotate-90" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>

                {open && (
                  <div className="border-t border-gray-100 bg-white px-4 py-4">
                    {/* Rule 1: the panel's own header row, primary top-right.
                        Inviting is per-artist, so it belongs on the artist, not
                        on the section header above. */}
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-800">
                          {artist.name}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Emails them a Merch Manager login for this event.
                        </p>
                      </div>
                      {/* Same treatment as the location card: Invite keeps the
                          primary slot on the right, Remove sits to its left as
                          red TEXT because it's destructive. SecondaryButton is
                          reserved for recovery actions in this file. No gap
                          class — PrimaryButton brings its own ml-2.

                          Shown even when locked, unlike the location card: an
                          UNLOCKED settlement blocks removal too and is
                          explained nowhere on this panel, so hiding the button
                          only for `locked` would teach the wrong rule. The
                          dialog states the reason in both cases. */}
                      <div className="flex shrink-0 items-center">
                        <button
                          type="button"
                          onClick={() => setRemoveArtist(artist)}
                          className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-40"
                        >
                          <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          Remove artist
                        </button>
                        <PrimaryButton
                          onClick={() =>
                            setInviteTarget({
                              vendorId: artist.id,
                              role: "artist",
                              vendorName: artist.name,
                            })
                          }
                        >
                          {isInvited("artist", artist.id)
                            ? "Resend invite"
                            : "Invite artist"}
                        </PrimaryButton>
                      </div>
                    </div>
                    {locked && (
                      <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2">
                        <p className="text-sm font-medium text-yellow-800">
                          This settlement is locked
                          {settlement?.locked_at
                            ? ` (${new Date(
                                settlement.locked_at
                              ).toLocaleString()})`
                            : ""}
                          .
                        </p>
                        <p className="mt-0.5 text-xs text-yellow-700">
                          The numbers have been signed — the split and the fee
                          lines can no longer be changed for this artist.
                        </p>
                      </div>
                    )}
                    {/* Setup before money: which locations exist and what they
                        sell from decides where Merch Manager's items land, so
                        it belongs above the split. Mounted only while the panel
                        is open — that's the per-artist lazy load. */}
                    <LocationMenuEditor
                      eventId={eventId}
                      artist={artist}
                      organizationId={organizationId}
                      locked={locked}
                      // Taking a location off another vendor can leave THEM with no
                      // location at this event, which is what puts a vendor on
                      // this tab at all — so the whole list has to be re-derived,
                      // not just this artist's setup.
                      onArtistsChanged={() => {
                        void loadAll();
                      }}
                    />
                    <SplitEditor
                      key={`split-${artist.id}-${deal?.id ?? "new"}`}
                      eventId={eventId}
                      artist={artist}
                      deal={deal}
                      managers={managers.filter((m) => m.id !== artist.id)}
                      managersAreEventScoped={managersAreEventScoped}
                      resolveVendorName={resolveVendorName}
                      onAddManager={() => setAddManagerOpen(true)}
                      onInvite={setInviteTarget}
                      isInvited={isInvited}
                      locked={locked}
                      onSaved={loadDeals}
                    />
                    <FeeEditor
                      eventId={eventId}
                      artist={artist}
                      settlement={settlement}
                      locked={locked}
                      onSaved={loadDeals}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MerchVendorDeals;
